import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Trash2, Printer, X, CheckCircle, XCircle, Edit2, Save, CreditCard, Loader2, Link2, QrCode, DollarSign, Undo2, Send, Mail, MessageSquare, Wallet, History } from 'lucide-react';
import { fmtLocalDate } from '../lib/timeFmt';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import AdminCompletionEditSheet, { canCorrectCompletion } from './AdminCompletionEditSheet';
import FieldCompletionDetails from './FieldCompletionDetails';
import DraftStatus from './DraftStatus';
import { useDurableDraft } from '../hooks/useDurableDraft';
import BillingFollowUpDialog, { FollowUpChip } from './BillingFollowUpDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter231 — labels for the separated payment disposition on the drawer.
const DISPOSITION_LABELS_UI = {
  paid_cash: 'Customer Paid — Cash',
  paid_check: 'Customer Paid — Check',
  paid_other: 'Customer Paid — Other',
  paid_card: 'Customer Paid — Card',
  deferred_unable_collect: 'No Payment Collected — Office to Bill',
  deferred_payment: 'No Payment Collected — Office to Bill',
  no_balance_due: 'No Customer Balance Due',
  no_balance: 'No Customer Balance Due',
  financing: 'Financing Used',
};

const computeLineTotal = (li) => Number(li.quantity || 0) * Number(li.unit_price || 0);
const computeAmount = (lines) => lines.reduce((acc, li) => acc + computeLineTotal(li), 0);
const makeKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `li_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
const withKey = (li) => (li && li._key ? li : { ...li, _key: makeKey() });
const normalizeLineItems = (doc) => {
  if (Array.isArray(doc.line_items) && doc.line_items.length) return doc.line_items.map(withKey);
  return [{ _key: makeKey(), description: doc.description || doc.notes || '', quantity: 1, unit_price: doc.amount || 0 }];
};

const STATUS_COLORS = {
  paid: 'text-green-500 bg-green-500/10',
  deposit_paid: 'text-blue-400 bg-blue-500/10',
  accepted: 'text-emerald-400 bg-emerald-500/10',
  approved: 'text-green-500 bg-green-500/10',
  pending: 'text-[#D4AF37] bg-[#D4AF37]/10',
  overdue: 'text-red-500 bg-red-500/10',
  declined: 'text-red-500 bg-red-500/10',
  rejected: 'text-red-500 bg-red-500/10',
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[status] || 'text-[#A3A3A3] bg-[#262626]'}`}>{(status || 'pending').replace('_', ' ').toUpperCase()}</span>
);

// iter165 — Short, badge-friendly labels for the payment method chip shown
// alongside the PAID status badge. Lowercase backend key → uppercase chip text.
const PAYMENT_METHOD_BADGE_LABELS = {
  cash: 'CASH',
  check: 'CHECK',
  card_in_person: 'CARD',
  stripe: 'CARD',
  stripe_webhook: 'CARD',
  stripe_public_link: 'CARD',
  ach: 'ACH',
  hsp: 'HSP',
  warranty: 'WARRANTY',
  other: 'OTHER',
};

const PaymentMethodBadge = ({ method, status }) => {
  if (!method) return null;
  const isPaidStatus = status === 'paid' || status === 'hsp_paid';
  if (!isPaidStatus) return null;
  const text = PAYMENT_METHOD_BADGE_LABELS[method] || String(method).replace(/_/g, ' ').toUpperCase();
  return (
    <span
      data-testid="payment-method-badge"
      title={`Paid via ${PAYMENT_METHOD_LABELS[method] || method}`}
      className="px-2 py-1 rounded text-xs font-bold text-green-500 bg-green-500/10 border border-green-500/30 uppercase tracking-wider"
    >
      {text}
    </span>
  );
};

// iter133 — Phase 1 (QuickBooks deferral): hide Nexflow-side email + Stripe payment
// buttons so office/field crew defer billing/payments to QuickBooks. Toggle this flag
// to re-expose the legacy buttons if needed.
const HIDE_NEXFLOW_BILLING_ACTIONS = true;

// iter133 — Phase 1: pretty-format payment method for the print view.
const PAYMENT_METHOD_LABELS = {
  cash: 'Cash',
  check: 'Check',
  card_in_person: 'Card (in person)',
  stripe: 'Card (online)',
  stripe_webhook: 'Card (online)',
  stripe_public_link: 'Card (online)',
  other: 'Other',
};
const formatPaymentMethod = (method, detail) => {
  if (!method) return '—';
  const base = PAYMENT_METHOD_LABELS[method] || method;
  return detail ? `${base} — ${detail}` : base;
};

// iter197 — Payment correction reason options (mirror backend PAYMENT_CORRECTION_REASONS).
const CORRECTION_REASONS = [
  { v: 'tech_wrong_amount', l: 'Tech entered wrong amount' },
  { v: 'customer_paid_different', l: 'Customer paid different amount' },
  { v: 'duplicate_payment_entry', l: 'Duplicate payment entry' },
  { v: 'office_verified_qb', l: 'Office verified QB payment' },
  { v: 'corrected_payment_method', l: 'Corrected payment method' },
  { v: 'other', l: 'Other' },
];
const CORRECTION_REASON_LABEL = Object.fromEntries(CORRECTION_REASONS.map((r) => [r.v, r.l]));

const PrintableDoc = ({ doc, type, computedSubtotal, remainingBalance, discountAmount, discountSource, discountPercent }) => {
  if (!doc) return null;
  const lineItems = normalizeLineItems(doc);
  const label = type === 'invoice' ? 'INVOICE' : 'ESTIMATE';
  const number = doc.invoice_number || doc.estimate_number || '';
  const deposit = Number(doc.deposit_amount || 0);
  const amountPaid = Number(doc.amount_paid || 0);
  const hasDiscount = (discountAmount || 0) > 0;
  // iter133 — Phase 1: paid-invoice details for print view.
  const isPaid = type === 'invoice' && doc.status === 'paid';
  const paidDate = doc.paid_at ? new Date(doc.paid_at).toLocaleDateString() : '';
  const paymentMethodLabel = formatPaymentMethod(doc.payment_method, doc.payment_method_detail);
  // iter226 — completeness fields for the printable customer invoice.
  const isWarranty = type === 'invoice' && doc.payor_kind === 'warranty';
  const serviceAddress = doc.address || doc.service_address || '';
  const billingAddress = doc.billing_address || '';
  const statusLabel = (doc.status === 'deposit_paid' ? 'PARTIALLY PAID' : (doc.status || '').replace(/_/g, ' ').toUpperCase());
  const taxAmount = Number(doc.tax_amount || doc.tax || 0);
  return (
    <div id="printable-doc">
      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #C18F2A', paddingBottom: 20, marginBottom: 30 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 32, color: '#C18F2A', fontWeight: 800 }}>NEXFLOW INC.</h1>
          <p style={{ margin: 0, fontSize: 11, letterSpacing: 2 }}>PREMIUM PLUMBING &amp; DRAIN CLEANING</p>
          <p style={{ margin: '6px 0 0', fontSize: 11 }}>614-285-4502 · team@nexflowplumbing.com</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <h2 className="print-gold" style={{ margin: 0, fontSize: 36, fontWeight: 800 }}>{label}</h2>
          <p style={{ margin: 0, fontSize: 14 }}>#{number}</p>
          <p style={{ margin: 0, fontSize: 12 }}>{fmtLocalDate(doc.service_date || doc.date)}</p>
          {isPaid ? (
            <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 800, color: '#059669', letterSpacing: 1 }}>
              STATUS: PAID
            </p>
          ) : (
            statusLabel && <p style={{ margin: '8px 0 0', fontSize: 12, fontWeight: 700, color: '#666', letterSpacing: 1 }}>STATUS: {statusLabel}</p>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 10, letterSpacing: 1.5, margin: 0, color: '#666' }}>BILL TO</p>
          <p style={{ fontSize: 16, fontWeight: 600, margin: '4px 0 0' }}>{doc.customer_name}</p>
          {billingAddress && <p style={{ fontSize: 12, margin: '2px 0 0', color: '#444' }}>{billingAddress}</p>}
          {serviceAddress && serviceAddress !== billingAddress && (
            <>
              <p style={{ fontSize: 10, letterSpacing: 1.5, margin: '10px 0 0', color: '#666' }}>SERVICE ADDRESS</p>
              <p style={{ fontSize: 13, margin: '2px 0 0', color: '#222' }}>{serviceAddress}</p>
            </>
          )}
        </div>
        <div style={{ textAlign: 'right', minWidth: 180 }}>
          {doc.technician_name && (
            <>
              <p style={{ fontSize: 10, letterSpacing: 1.5, margin: 0, color: '#666' }}>TECHNICIAN</p>
              <p style={{ fontSize: 13, fontWeight: 600, margin: '2px 0 0' }}>{doc.technician_name}</p>
            </>
          )}
        </div>
      </div>
      {isWarranty && (
        <div style={{ marginBottom: 20, padding: 12, border: '1px solid #ddd', borderRadius: 6, background: '#faf7ef' }}>
          <p style={{ fontSize: 10, letterSpacing: 1.5, margin: 0, color: '#666' }}>WARRANTY / HOME SERVICE PLAN</p>
          <p style={{ fontSize: 13, fontWeight: 600, margin: '4px 0 0' }}>Payor: {doc.customer_name || 'Home Service Plan'}</p>
          {doc.homeowner_name && <p style={{ fontSize: 12, margin: '2px 0 0', color: '#444' }}>For customer: {doc.homeowner_name}</p>}
          {doc.homeowner_address && <p style={{ fontSize: 11, margin: '2px 0 0', color: '#555' }}>{doc.homeowner_address}</p>}
        </div>
      )}
      <table>
        <thead><tr><th>Description</th><th style={{ textAlign: 'right', width: 80 }}>Qty</th><th style={{ textAlign: 'right', width: 120 }}>Unit Price</th><th style={{ textAlign: 'right', width: 120 }}>Total</th></tr></thead>
        <tbody>
          {lineItems.map((li, i) => (
            <tr key={li._key || i}>
              <td>{li.description}</td>
              <td style={{ textAlign: 'right' }}>{li.quantity}</td>
              <td style={{ textAlign: 'right' }}>${Number(li.unit_price).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>${computeLineTotal(li).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="print-totals" style={{ marginTop: 16, marginLeft: 'auto', width: 280 }}>
        <tbody>
          <tr><td>Subtotal</td><td style={{ textAlign: 'right' }}>${computedSubtotal.toFixed(2)}</td></tr>
          {hasDiscount && <tr style={{ color: '#059669' }}><td>{discountSource || 'Discount'} ({discountPercent}%)</td><td style={{ textAlign: 'right' }}>-${discountAmount.toFixed(2)}</td></tr>}
          {deposit > 0 && <tr><td>Deposit Required</td><td style={{ textAlign: 'right' }}>${deposit.toFixed(2)}</td></tr>}
          {taxAmount > 0 && <tr><td>Tax</td><td style={{ textAlign: 'right' }}>${taxAmount.toFixed(2)}</td></tr>}
          {amountPaid > 0 && <tr style={{ color: '#059669' }}><td>Amount Paid</td><td style={{ textAlign: 'right' }}>-${amountPaid.toFixed(2)}</td></tr>}
          <tr style={{ fontWeight: 800, fontSize: 18, borderTop: '2px solid #C18F2A' }}>
            <td className="print-gold" style={{ paddingTop: 10 }}>{isPaid ? 'BALANCE' : (amountPaid > 0 ? 'BALANCE' : 'TOTAL')}</td>
            <td className="print-gold" style={{ textAlign: 'right', paddingTop: 10 }}>${isPaid ? '0.00' : (remainingBalance ?? computedSubtotal).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
      {/* iter133 — Phase 1: paid-invoice payment block on the print view. */}
      {isPaid && (
        <table style={{ marginTop: 12, marginLeft: 'auto', width: 280, fontSize: 12 }}>
          <tbody>
            <tr><td style={{ color: '#666' }}>Payment Method</td><td style={{ textAlign: 'right' }}>{paymentMethodLabel}</td></tr>
            {paidDate && <tr><td style={{ color: '#666' }}>Payment Date</td><td style={{ textAlign: 'right' }}>{paidDate}</td></tr>}
          </tbody>
        </table>
      )}
      {doc.notes && (
        <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #ddd' }}>
          <p style={{ fontSize: 10, letterSpacing: 1.5, margin: 0, color: '#666' }}>NOTES</p>
          <p style={{ fontSize: 13, marginTop: 6, whiteSpace: 'pre-wrap' }}>{doc.notes}</p>
        </div>
      )}
      <div style={{ marginTop: 48, fontSize: 10, color: '#666', textAlign: 'center', borderTop: '1px solid #ddd', paddingTop: 12 }}>
        Thank you for choosing Nexflow Inc. — Local. Trusted. Reliable.
      </div>
    </div>
  );
};

// iter138 — Phase 4: Full Office Packet view.
// Sibling of PrintableDoc; activated by the "Print Full Packet" button. Includes the
// complete customer/job/billing snapshot office needs to enter the invoice into
// QuickBooks without bouncing through the schedule or customer page.
const PrintablePacket = ({ doc, type, computedSubtotal, remainingBalance, discountAmount, discountSource, discountPercent }) => {
  if (!doc) return null;
  const lineItems = normalizeLineItems(doc);
  const label = type === 'invoice' ? 'INVOICE PACKET' : 'ESTIMATE PACKET';
  const number = doc.invoice_number || doc.estimate_number || '';
  const deposit = Number(doc.deposit_amount || 0);
  const amountPaid = Number(doc.amount_paid || 0);
  const hasDiscount = (discountAmount || 0) > 0;
  const isPaid = type === 'invoice' && doc.status === 'paid';
  const paidDate = doc.paid_at ? new Date(doc.paid_at).toLocaleDateString() : '';
  const paymentMethodLabel = formatPaymentMethod(doc.payment_method, doc.payment_method_detail);
  const isWarranty = doc.payor_kind === 'warranty';
  const officeBillingLabels = {
    needs_review: 'Needs Review',
    ready_for_qb: 'Ready for QB',
    entered_in_qb: 'Entered in QB',
    on_hold: 'On Hold',
  };
  const hspStatusLabels = {
    pending_office_review: 'Pending Office Review',
    submitted_to_hsp: 'Submitted to HSP',
    partially_paid: 'Partially Paid',
    hsp_paid: 'HSP Paid',
  };
  const sectionLabel = { fontSize: 10, letterSpacing: 1.5, margin: 0, color: '#777', textTransform: 'uppercase', fontWeight: 700 };
  const sectionWrap = { marginTop: 16, paddingTop: 12, borderTop: '1px solid #ddd' };
  const kvRow = { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' };
  return (
    <div id="printable-packet" style={{ display: 'none' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #C18F2A', paddingBottom: 16, marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, color: '#C18F2A', fontWeight: 800 }}>NEXFLOW INC.</h1>
          <p style={{ margin: 0, fontSize: 10, letterSpacing: 2 }}>PREMIUM PLUMBING &amp; DRAIN CLEANING</p>
          <p style={{ margin: '4px 0 0', fontSize: 10 }}>614-285-4502 · team@nexflowplumbing.com</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <h2 className="print-gold" style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>{label}</h2>
          {number && <p style={{ margin: 0, fontSize: 13 }}>#{number}</p>}
          <p style={{ margin: 0, fontSize: 11 }}>{fmtLocalDate(doc.service_date || doc.date)}</p>
          {isPaid && (
            <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: 800, color: '#059669', letterSpacing: 1 }}>STATUS: PAID</p>
          )}
          {isWarranty && (
            <p style={{ margin: '4px 0 0', fontSize: 11, fontWeight: 700, color: '#4f46e5', letterSpacing: 0.5 }}>WARRANTY / HSP CLAIM</p>
          )}
        </div>
      </div>

      {/* Two-column customer + job info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 4 }}>
        <div>
          <p style={sectionLabel}>{isWarranty ? 'Billed To (Warranty Co.)' : 'Customer'}</p>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '4px 0 0' }}>{doc.customer_name || '—'}</p>
          {doc.customer_phone && <p style={{ fontSize: 12, margin: '2px 0 0' }}>📞 {doc.customer_phone}</p>}
          {doc.customer_email && <p style={{ fontSize: 12, margin: '2px 0 0' }}>✉ {doc.customer_email}</p>}
          {doc.billing_address && <p style={{ fontSize: 12, margin: '4px 0 0', color: '#555' }}>{doc.billing_address}</p>}
          {doc.address && doc.address !== doc.billing_address && (
            <>
              <p style={{ ...sectionLabel, marginTop: 10 }}>Service Address</p>
              <p style={{ fontSize: 12, margin: '2px 0 0', color: '#222' }}>{doc.address}</p>
            </>
          )}
        </div>
        <div>
          <p style={sectionLabel}>Job Info</p>
          <div style={kvRow}><span style={{ color: '#666' }}>Job Date</span><span style={{ fontWeight: 600 }}>{fmtLocalDate(doc.service_date || doc.date) || '—'}</span></div>
          {doc.technician_name && <div style={kvRow}><span style={{ color: '#666' }}>Technician</span><span style={{ fontWeight: 600 }}>{doc.technician_name}</span></div>}
          {(doc.work_order_number || doc.hsp_work_order_number) && (
            <div style={kvRow}><span style={{ color: '#666' }}>Work Order #</span><span style={{ fontFamily: 'monospace' }}>{doc.hsp_work_order_number || doc.work_order_number}</span></div>
          )}
          {doc.service_type && <div style={kvRow}><span style={{ color: '#666' }}>Service Type</span><span>{doc.service_type}</span></div>}
          {doc.department && <div style={kvRow}><span style={{ color: '#666' }}>Department</span><span>{doc.department}</span></div>}
        </div>
      </div>

      {/* Warranty / HSP block (conditional) */}
      {isWarranty && (
        <div style={sectionWrap}>
          <p style={sectionLabel}>Warranty / HSP Details</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 6 }}>
            <div>
              <p style={{ ...sectionLabel, marginTop: 0 }}>Residential Homeowner</p>
              <p style={{ fontSize: 13, fontWeight: 600, margin: '2px 0 0' }}>{doc.homeowner_name || '—'}</p>
              {doc.homeowner_address && <p style={{ fontSize: 11, margin: '2px 0 0', color: '#555' }}>{doc.homeowner_address}</p>}
              {doc.homeowner_phone && <p style={{ fontSize: 11, margin: '2px 0 0' }}>📞 {doc.homeowner_phone}</p>}
              {doc.homeowner_email && <p style={{ fontSize: 11, margin: '2px 0 0' }}>✉ {doc.homeowner_email}</p>}
            </div>
            <div>
              <p style={{ ...sectionLabel, marginTop: 0 }}>HSP Status</p>
              <p style={{ fontSize: 13, fontWeight: 600, margin: '2px 0 0' }}>{hspStatusLabels[doc.hsp_review_status] || (doc.hsp_review_status || '—')}</p>
              {doc.hsp_paid_at && <div style={kvRow}><span style={{ color: '#666' }}>HSP Paid Date</span><span>{new Date(doc.hsp_paid_at).toLocaleDateString()}</span></div>}
              {Array.isArray(doc.hsp_payments) && doc.hsp_payments.length > 0 && (
                <>
                  <p style={{ fontSize: 10, color: '#666', marginTop: 8, textTransform: 'uppercase', letterSpacing: 1 }}>HSP Payments</p>
                  {doc.hsp_payments.map((p, i) => (
                    <div key={i} style={{ ...kvRow, fontSize: 11 }}>
                      <span>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : ''} · {(p.method || '').toUpperCase()}{p.reference ? ` #${p.reference}` : ''}</span>
                      <span style={{ fontWeight: 700 }}>${Number(p.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Work performed / description */}
      {(doc.description || doc.completion_notes) && (
        <div style={sectionWrap}>
          <p style={sectionLabel}>Work Performed</p>
          <p style={{ fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{doc.description || doc.completion_notes}</p>
        </div>
      )}

      {/* Materials used */}
      {doc.materials_used && (
        <div style={sectionWrap}>
          <p style={sectionLabel}>Materials Used</p>
          <p style={{ fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{doc.materials_used}</p>
        </div>
      )}

      {/* Line items */}
      <div style={sectionWrap}>
        <p style={sectionLabel}>Line Items</p>
        <table style={{ marginTop: 6 }}>
          <thead><tr><th>Description</th><th style={{ textAlign: 'right', width: 60 }}>Qty</th><th style={{ textAlign: 'right', width: 100 }}>Unit Price</th><th style={{ textAlign: 'right', width: 100 }}>Total</th></tr></thead>
          <tbody>
            {lineItems.map((li, i) => (
              <tr key={li._key || i}>
                <td>{li.description}</td>
                <td style={{ textAlign: 'right' }}>{li.quantity}</td>
                <td style={{ textAlign: 'right' }}>${Number(li.unit_price).toFixed(2)}</td>
                <td style={{ textAlign: 'right' }}>${computeLineTotal(li).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals + payment + internal office status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, marginTop: 12 }}>
        <div>
          {(doc.office_billing_status || doc.office_billing_status_updated_at) && (
            <div style={{ border: '1px dashed #C18F2A', padding: 8, borderRadius: 4, background: '#fffbeb' }}>
              <p style={{ ...sectionLabel, color: '#C18F2A' }}>Internal — Office Billing Status</p>
              <p style={{ fontSize: 12, fontWeight: 700, margin: '4px 0 0' }}>{officeBillingLabels[doc.office_billing_status] || doc.office_billing_status || 'Needs Review'}</p>
              {doc.office_billing_status_updated_by_name && (
                <p style={{ fontSize: 10, color: '#666', margin: '2px 0 0' }}>
                  Last updated by {doc.office_billing_status_updated_by_name}
                  {doc.office_billing_status_updated_at ? ` · ${new Date(doc.office_billing_status_updated_at).toLocaleString()}` : ''}
                </p>
              )}
              <p style={{ fontSize: 9, color: '#999', margin: '6px 0 0', fontStyle: 'italic' }}>
                QuickBooks is the official accounting system. This status is NexFlow internal only.
              </p>
            </div>
          )}
        </div>
        <table className="print-totals">
          <tbody>
            <tr><td>Subtotal</td><td style={{ textAlign: 'right' }}>${computedSubtotal.toFixed(2)}</td></tr>
            {hasDiscount && <tr style={{ color: '#059669' }}><td>{discountSource || 'Discount'} ({discountPercent}%)</td><td style={{ textAlign: 'right' }}>-${discountAmount.toFixed(2)}</td></tr>}
            {deposit > 0 && <tr><td>Deposit Required</td><td style={{ textAlign: 'right' }}>${deposit.toFixed(2)}</td></tr>}
            {amountPaid > 0 && <tr style={{ color: '#059669' }}><td>Amount Paid</td><td style={{ textAlign: 'right' }}>-${amountPaid.toFixed(2)}</td></tr>}
            <tr style={{ fontWeight: 800, fontSize: 16, borderTop: '2px solid #C18F2A' }}>
              <td className="print-gold" style={{ paddingTop: 8 }}>{isPaid ? 'BALANCE' : (amountPaid > 0 ? 'BALANCE' : 'TOTAL')}</td>
              <td className="print-gold" style={{ textAlign: 'right', paddingTop: 8 }}>${isPaid ? '0.00' : (remainingBalance ?? computedSubtotal).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment block (only when paid) */}
      {isPaid && (
        <div style={{ marginTop: 12, marginLeft: 'auto', width: 280 }}>
          <table style={{ fontSize: 11 }}>
            <tbody>
              <tr><td style={{ color: '#666' }}>Payment Method</td><td style={{ textAlign: 'right' }}>{paymentMethodLabel}</td></tr>
              {paidDate && <tr><td style={{ color: '#666' }}>Payment Date</td><td style={{ textAlign: 'right' }}>{paidDate}</td></tr>}
              {doc.payment_reference && <tr><td style={{ color: '#666' }}>Reference</td><td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{doc.payment_reference}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      {doc.notes && (
        <div style={sectionWrap}>
          <p style={sectionLabel}>Notes</p>
          <p style={{ fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{doc.notes}</p>
        </div>
      )}

      <div style={{ marginTop: 32, fontSize: 9, color: '#999', textAlign: 'center', borderTop: '1px solid #ddd', paddingTop: 10 }}>
        Internal office packet · Nexflow Inc. · QuickBooks remains the official accounting record.
      </div>
    </div>
  );
};

const BillingDetailSheet = ({ open, onOpenChange, doc, type, viewerRole, onChange, editOnOpen = false, printOnOpen = null }) => {
  const { hasPermission } = useAuth();
  const [editing, setEditing] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [depositAmount, setDepositAmount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [amountPaidLocal, setAmountPaidLocal] = useState(0);
  // Promo / membership discount
  const [memberDiscountActive, setMemberDiscountActive] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_percent, vip_name}
  const [validatingPromo, setValidatingPromo] = useState(false);
  // Staff actions
  const [staffPromoInput, setStaffPromoInput] = useState('');
  const [staffApplyingCode, setStaffApplyingCode] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [markPaidMethod, setMarkPaidMethod] = useState('cash');
  const [markPaidSource, setMarkPaidSource] = useState('office');
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [markPaidRef, setMarkPaidRef] = useState('');
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidDate, setMarkPaidDate] = useState('');
  const [markPaidNotes, setMarkPaidNotes] = useState('');
  // iter140 — Phase 5: residential receipt auto-send opt-out.
  const [markPaidSendReceipt, setMarkPaidSendReceipt] = useState(true);
  const [showRefund, setShowRefund] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  // iter134 — Phase 2: HSP / Warranty Mark-Paid state.
  const [showHspPaid, setShowHspPaid] = useState(false);
  const [hspAmount, setHspAmount] = useState('');
  const [hspDate, setHspDate] = useState('');
  const [hspMethod, setHspMethod] = useState('check');
  const [hspReference, setHspReference] = useState('');
  const [hspNotes, setHspNotes] = useState('');
  const [hspSavingStatus, setHspSavingStatus] = useState(false);
  const [hspSavingPaid, setHspSavingPaid] = useState(false);
  // iter136 — Phase 3: Office Billing (QuickBooks) Status state.
  const [officeBillingNote, setOfficeBillingNote] = useState('');
  const [officeBillingSaving, setOfficeBillingSaving] = useState(false);
  const [officeBillingHistoryOpen, setOfficeBillingHistoryOpen] = useState(false);
  // iter258 — QuickBooks external invoice # (office accounting reference).
  const [qbInvoiceNumber, setQbInvoiceNumber] = useState('');
  const [qbSaving, setQbSaving] = useState(false);
  // iter91 — Send by Email modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendToEmail, setSendToEmail] = useState('');
  const [sendIncludeSms, setSendIncludeSms] = useState(true);
  const [sendSmsMessage, setSendSmsMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  // iter94 typo guard — when backend returns EMAIL_TYPO_SUSPECTED, surface "Did you mean …?"
  const [typoSuggestion, setTypoSuggestion] = useState(null);  // {to, suggestion, reason}
  // iter109 — Editable estimate number (QuickBooks migration prep). Only admin / SM / super
  // can flip this; the pencil affordance hides for everyone else.
  const { user } = useAuth();
  const [editingNumber, setEditingNumber] = useState(false);
  const [numberDraft, setNumberDraft] = useState('');
  const [savingNumber, setSavingNumber] = useState(false);
  // iter116 — Admin Completion Correction Sheet. Opens when invoice has a
  // completion_id (i.e. was created from a job completion) and viewer is allowed.
  const [editingCompletionId, setEditingCompletionId] = useState(null);

  // iter197 — Payment Correction workflow state.
  const [showCorrectPayment, setShowCorrectPayment] = useState(false);
  const [corrAmount, setCorrAmount] = useState('');
  const [corrOnSite, setCorrOnSite] = useState('');
  const [corrDisposition, setCorrDisposition] = useState('');
  const [corrMethod, setCorrMethod] = useState('');
  const [corrMethodDetail, setCorrMethodDetail] = useState('');
  const [corrReference, setCorrReference] = useState('');
  const [corrNotes, setCorrNotes] = useState('');
  const [corrDate, setCorrDate] = useState('');
  const [corrStatus, setCorrStatus] = useState('');   // '' = auto
  const [corrReason, setCorrReason] = useState('');
  const [corrReasonNote, setCorrReasonNote] = useState('');
  const [corrSaving, setCorrSaving] = useState(false);

  useEffect(() => {
    if (doc) {
      setLineItems(normalizeLineItems(doc).map(li => ({ ...li })));
      setNotes(doc.notes || doc.description || '');
      setStatus(doc.status || '');
      setDepositAmount(doc.deposit_amount || 0);
      setQbInvoiceNumber(doc.quickbooks_invoice_number || '');
      setAmountPaidLocal(doc.amount_paid || 0);
      // iter129 — When the parent freshly created this doc, drop right into edit
      // mode so techs/managers can immediately type line items without hunting
      // for the Edit button (fixes the Austin "can't enter line items" report).
      setEditing(!!editOnOpen && viewerRole !== 'customer');
      setMemberDiscountActive(!!doc.customer_member_discount_active);
      setPromoApplied(doc.promo_code ? { code: doc.promo_code, discount_percent: doc.discount_percent || 10 } : null);
      setPromoInput('');
      setStaffPromoInput('');
      setPaymentLink('');
      setShowQr(false);
      setShowRefund(false);
      setRefundReason('');
      setRefundAmount('');
      setMarkPaidMethod('cash');
      setMarkPaidRef('');
      setMarkPaidAmount('');
      setMarkPaidDate('');
      setMarkPaidNotes('');
      // iter140 — Phase 5: default Send Receipt ON for residential, OFF for warranty/commercial/construction.
      const _pk = (doc?.payor_kind || '').toLowerCase();
      const _pt = (doc?.payor_type || '').toLowerCase();
      const _nonResidential = ['warranty', 'commercial', 'construction', 'internal'];
      setMarkPaidSendReceipt(!(_nonResidential.includes(_pk) || _nonResidential.includes(_pt) || (doc?.invoice_status || '').startsWith('hsp')));
      // iter134 — Phase 2 reset HSP mark-paid fields per doc.
      setShowHspPaid(false);
      setHspAmount('');
      setHspDate('');
      setHspMethod('check');
      setHspReference('');
      setHspNotes('');
      // iter136 — Phase 3: reset office billing form fields on doc change.
      setOfficeBillingNote('');
      setOfficeBillingHistoryOpen(false);
      // iter109 — reset estimate-number edit state when the doc changes.
      setEditingNumber(false);
      setNumberDraft(doc.estimate_number || doc.invoice_number || '');
    }
  }, [doc]);

  const savedSnapRef = React.useRef(null);
  useEffect(() => { savedSnapRef.current = null; }, [doc]);
  // Durable edit draft for an existing invoice/estimate (only once something actually changed).
  const stripKeys = (arr) => (arr || []).map(({ _key, ...li }) => li);
  const editSnap = { lineItems: stripKeys(lineItems), notes, status, depositAmount };
  const docSnapSer = doc ? JSON.stringify({ lineItems: stripKeys(normalizeLineItems(doc)), notes: doc.notes || doc.description || '',
    status: doc.status || '', depositAmount: doc.deposit_amount || 0 }) : '';
  const editDraft = useDurableDraft({
    scopeKey: doc?.id ? `billing_edit:${type || 'invoice'}:${doc.id}` : null,
    enabled: !!doc?.id && viewerRole !== 'customer',
    value: editing && JSON.stringify(editSnap) !== docSnapSer ? editSnap : null,
    onRestore: (d) => {
      if (!d || !Array.isArray(d.lineItems)) return;
      setLineItems(d.lineItems.map((li) => ({ ...li, _key: makeKey() })));
      setNotes(d.notes || '');
      if (d.status) setStatus(d.status);
      setDepositAmount(d.depositAmount || 0);
      setEditing(true);
    },
  });

  // iter240 — Persistent Payment History for the invoice drawer.
  const fetchInvoicePayments = async () => {
    if (type !== 'invoice' || !doc?.id) { setInvoicePayments([]); return; }
    try {
      const { data } = await axios.get(`${API}/invoices/${doc.id}/payments`, { withCredentials: true });
      setInvoicePayments(data?.payments || []);
    } catch { setInvoicePayments([]); }
  };
  useEffect(() => { if (open) fetchInvoicePayments(); /* eslint-disable-next-line */ }, [open, doc?.id]);


  // iter197/iter226 — Auto-print (customer invoice) when opened from the list Print
  // action. Verify the printable invoice actually rendered before calling print so we
  // never open a blank print window.
  useEffect(() => {
    if (open && printOnOpen && doc) {
      const t = setTimeout(() => {
        const node = document.getElementById('printable-doc');
        const ready = node && (node.textContent || '').trim().length > 20;
        if (!ready) {
          toast.error('Unable to prepare this invoice for printing. Please try again.');
          return;
        }
        window.print();
      }, 450);
      return () => clearTimeout(t);
    }
  }, [open, printOnOpen, doc]);

  if (!doc) return null;

  const isInvoice = type === 'invoice';
  const number = doc.invoice_number || doc.estimate_number;
  const canEdit = viewerRole !== 'customer';
  const subtotal = computeAmount(lineItems);
  const amountPaid = Number(amountPaidLocal || 0);
  const deposit = Number(depositAmount || 0);
  // Effective discount: membership beats promo. Only applies to invoices for customers.
  const effectiveDiscountPercent = isInvoice && viewerRole === 'customer'
    ? (memberDiscountActive ? 10 : (promoApplied ? Number(promoApplied.discount_percent || 0) : 0))
    : 0;
  const discountSource = memberDiscountActive ? 'Membership' : (promoApplied ? `Promo: ${promoApplied.code}` : null);
  const discountAmount = Number((subtotal * effectiveDiscountPercent / 100).toFixed(2));
  const discountedTotal = Number((subtotal - discountAmount).toFixed(2));
  const discountedDeposit = Number((deposit * (1 - effectiveDiscountPercent / 100)).toFixed(2));
  const remainingBalance = Math.max(0, discountedTotal - amountPaid);
  const remainingDeposit = Math.max(0, discountedDeposit - amountPaid);

  // iter134 — Phase 2: HSP / warranty invoice context.
  const isWarrantyInvoice = isInvoice && doc.payor_kind === 'warranty';
  const hspReviewStatus = doc.hsp_review_status || 'pending_office_review';
  const HSP_STATUS_LABEL = {
    pending_office_review: 'Pending Office Review',
    submitted_to_hsp: 'Submitted to HSP',
    partially_paid: 'Partially Paid',
    hsp_paid: 'HSP Paid',
  };
  const HSP_STATUS_COLOR = {
    pending_office_review: 'text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30',
    submitted_to_hsp: 'text-blue-300 bg-blue-500/10 border-blue-500/30',
    partially_paid: 'text-orange-300 bg-orange-500/10 border-orange-500/30',
    hsp_paid: 'text-green-400 bg-green-500/10 border-green-500/30',
  };
  const hspPaymentsList = Array.isArray(doc.hsp_payments) ? doc.hsp_payments : [];

  // iter136 — Phase 3: Office Billing (QuickBooks) Status context.
  const OFFICE_BILLING_OPTIONS = [
    { key: 'needs_review',  label: 'Needs Review',   color: 'text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30' },
    { key: 'ready_for_qb',  label: 'Ready for QB',   color: 'text-blue-300 bg-blue-500/10 border-blue-500/30' },
    { key: 'entered_in_qb', label: 'Entered in QB',  color: 'text-green-400 bg-green-500/10 border-green-500/30' },
    { key: 'paid',          label: 'Paid',           color: 'text-green-300 bg-green-500/15 border-green-500/40' },
    { key: 'on_hold',       label: 'On Hold',        color: 'text-orange-300 bg-orange-500/10 border-orange-500/30' },
  ];
  const OFFICE_BILLING_ROLES_FE = new Set(['super_admin', 'admin', 'service_manager', 'office_staff', 'manager']);
  const canManageOfficeBilling = isInvoice && OFFICE_BILLING_ROLES_FE.has(user?.role);
  const officeBillingStatus = doc.office_billing_status || 'needs_review';
  const officeBillingHistory = Array.isArray(doc.office_billing_history) ? doc.office_billing_history : [];
  const officeBillingMeta = OFFICE_BILLING_OPTIONS.find((o) => o.key === officeBillingStatus) || OFFICE_BILLING_OPTIONS[0];

  const updateLine = (idx, field, value) => {
    const next = [...lineItems];
    next[idx] = { ...next[idx], [field]: value };
    setLineItems(next);
  };
  const addLine = () => setLineItems([...lineItems, { _key: makeKey(), description: '', quantity: 1, unit_price: 0, kind: 'service', warranty_covered: false }]);
  const removeLine = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));

  const handlePrint = () => window.print();
  // iter138 — Phase 4: Print Full Packet (toggles a body class so the @media print
  // rules switch from #printable-doc to #printable-packet). Falls back to a 4s
  // timeout cleanup in case the browser never fires `afterprint` (Safari/iOS quirks).
  const handlePrintPacket = () => {
    document.body.classList.add('nf-print-packet');
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      document.body.classList.remove('nf-print-packet');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    // Safety net — clear the class even if afterprint never fires.
    setTimeout(cleanup, 4000);
    setTimeout(() => window.print(), 50);
  };

  // iter109 — Editable estimate number. Backend is admin/SM/super-only and
  // enforces uniqueness + audit log. We mirror that role check here purely for UX
  // (hide the pencil for techs/office staff); the backend stays the source of truth.
  const canEditEstimateNumber = !isInvoice && ['super_admin', 'admin', 'service_manager'].includes(user?.role);
  const handleSaveNumber = async () => {
    const next = (numberDraft || '').trim();
    if (!next) {
      toast.error('Estimate number cannot be empty');
      return;
    }
    if (next === (doc.estimate_number || '')) {
      setEditingNumber(false);
      return;
    }
    setSavingNumber(true);
    try {
      await axios.patch(
        `${API}/admin/estimates/${doc.id}/number`,
        { estimate_number: next },
        { withCredentials: true },
      );
      toast.success(`Estimate number updated to ${next}`);
      setEditingNumber(false);
      onChange?.();
    } catch (err) {
      // 409 → duplicate; backend already returns the exact copy the user spec'd.
      toast.error(err.response?.data?.detail || 'Failed to update number');
    }
    setSavingNumber(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        line_items: lineItems.map(li => ({
          description: li.description,
          quantity: Number(li.quantity) || 0,
          unit_price: Number(li.unit_price) || 0,
          // iter150 — preserve line kind + warranty flag on every save so the
          // backend can re-run membership discount math correctly.
          kind: li.kind || 'service',
          warranty_covered: !!li.warranty_covered,
        })),
        notes,
        status,
        deposit_amount: Number(depositAmount) || 0,
      };
      const url = isInvoice ? `${API}/invoices/${doc.id}` : `${API}/estimates/${doc.id}/edit`;
      await axios.put(url, payload, { withCredentials: true });
      toast.success('Saved');
      savedSnapRef.current = { lineItems, notes, status, depositAmount };
      await editDraft.clear();
      setEditing(false);
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  // iter258 — Save the QuickBooks external invoice # independently of the main
  // invoice edit. Office/admin accounting reference only.
  const handleSaveQbNumber = async () => {
    setQbSaving(true);
    try {
      await axios.put(`${API}/invoices/${doc.id}`, { quickbooks_invoice_number: qbInvoiceNumber.trim() }, { withCredentials: true });
      if (doc) doc.quickbooks_invoice_number = qbInvoiceNumber.trim();
      toast.success('QuickBooks Invoice # saved');
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setQbSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      const url = isInvoice ? `${API}/invoices/${doc.id}` : `${API}/estimates/${doc.id}`;
      await axios.delete(url, { withCredentials: true });
      toast.success('Deleted');
      onOpenChange(false);
      onChange?.();
    } catch { toast.error('Delete failed'); }
  };

  // iter91 — Send Invoice/Estimate by Email (with optional SMS follow-up).
  const openSendModal = async () => {
    // Default to whatever's on the invoice
    let email = doc.customer_email || '';
    let phone = doc.customer_phone || '';
    let name  = doc.customer_name  || '';
    // iter91.1 — If the invoice is missing fields, hydrate from the customer record itself
    // so the modal always shows the FULL customer info (name + email + phone).
    if (doc.customer_id && (!email || !phone)) {
      try {
        const { data: c } = await axios.get(`${API}/admin/customers/${doc.customer_id}`, { withCredentials: true });
        email = email || c?.email || '';
        phone = phone || c?.phone || '';
        name  = name  || [c?.first_name, c?.last_name].filter(Boolean).join(' ') || c?.company_name || name;
        // Keep the local doc copy in sync so the modal preview reads cleanly
        doc.customer_email = email;
        doc.customer_phone = phone;
        if (!doc.customer_name) doc.customer_name = name;
      } catch (err) {
        /* non-fatal — modal still opens, recipient just empty */
      }
    }
    setSendToEmail(email);
    setSendIncludeSms(true);
    setSendSmsMessage('');
    setSendResult(null);
    setTypoSuggestion(null);
    setShowSendModal(true);
  };

  const handleSendEmail = async ({ forceSend = false, updateCustomer = false, overrideTo = null } = {}) => {
    const recipient = (overrideTo || sendToEmail || '').trim().toLowerCase();
    if (!recipient || !recipient.includes('@')) {
      toast.error('Enter a valid recipient email');
      return;
    }
    setSending(true);
    setSendResult(null);
    setTypoSuggestion(null);
    try {
      const url = isInvoice
        ? `${API}/admin/invoices/${doc.id}/send-email`
        : `${API}/admin/estimates/${doc.id}/send-email`;
      const body = {
        to_email: recipient,
        send_sms: !!sendIncludeSms,
        sms_message: sendSmsMessage.trim() || null,
        force_send: !!forceSend,
        update_customer_email: !!updateCustomer,
      };
      const { data } = await axios.post(url, body, { withCredentials: true });
      // iter94 — typo guard short-circuit. Show the "Did you mean …?" prompt instead of toasting.
      if (data?.code === 'EMAIL_TYPO_SUSPECTED') {
        setTypoSuggestion({ to: data.to, suggestion: data.suggestion, reason: data.reason });
        setSending(false);
        return;
      }
      setSendResult(data);
      if (data?.email?.sent) {
        const fixedMsg = data?.customer_email_updated ? ' Customer email updated on file.' : '';
        if (data?.sms && !data.sms.sent) {
          toast.warning(`Email sent.${fixedMsg} SMS skipped: ${data.sms.error || data.sms.reason || 'see details'}`);
        } else if (data?.sms?.sent) {
          toast.success(`Email + SMS sent to ${recipient}.${fixedMsg}`);
        } else {
          toast.success(`Email sent to ${recipient}.${fixedMsg}`);
        }
        onChange?.();
      } else {
        toast.error(data?.email?.error || 'Email send failed — see details below');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Send failed';
      setSendResult({ email: { sent: false, error: msg } });
      toast.error(msg);
    }
    setSending(false);
  };

  // Customer estimate approve/reject
  const handleEstimateStatus = async (newStatus) => {
    try {
      const { data } = await axios.put(`${API}/estimates/${doc.id}`, { status: newStatus }, { withCredentials: true });
      if (newStatus === 'approved' && data?.invoice_created) {
        toast.success(`Approved — invoice ${data.invoice_created.invoice_number} created`);
      } else {
        toast.success(`Estimate ${newStatus}`);
      }
      setStatus(newStatus);
      onChange?.();
      onOpenChange(false);
    } catch (err) { toast.error('Failed'); }
  };

  // Office customer-release control (iter217)
  const handleRelease = async (action) => {
    let reason = '';
    if (['return', 'hold', 'withdraw'].includes(action)) {
      const labels = { return: 'Return for Correction', hold: 'Put On Hold', withdraw: 'Withdraw From Customer Portal' };
      reason = window.prompt(`Reason to ${labels[action]}:`) || '';
      if (!reason.trim()) { toast.error('A reason is required.'); return; }
    }
    try {
      const path = isInvoice ? 'invoices' : 'estimates';
      const { data } = await axios.post(`${API}/admin/${path}/${doc.id}/release`, { action, reason }, { withCredentials: true });
      const msg = { approve: 'Approved & released to customer', return: 'Returned for correction', hold: 'Put on hold', withdraw: 'Withdrawn from customer portal' }[action];
      toast.success(msg);
      if (doc) { doc.customer_release_status = data.customer_release_status; doc.customer_visible = data.customer_visible; }
      onChange?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Release action failed'); }
  };

  // Customer invoice accept/decline
  const handleInvoiceResponse = async (action) => {
    try {
      const { data } = await axios.put(`${API}/invoices/${doc.id}/respond`, { action }, { withCredentials: true });
      toast.success(`Invoice ${data.status}`);
      setStatus(data.status);
      onChange?.();
    } catch (err) { toast.error('Failed'); }
  };

  const handleValidatePromo = async () => {
    if (!promoInput.trim()) return;
    setValidatingPromo(true);
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code: promoInput.trim() }, { withCredentials: true });
      if (data.valid) {
        setPromoApplied({ code: promoInput.trim().toUpperCase(), discount_percent: data.discount_percent, vip_name: data.vip_name });
        toast.success(`Promo applied — ${data.discount_percent}% off (referred by ${data.vip_name})`);      } else {
        toast.error(data.reason || 'Invalid code');
        setPromoApplied(null);
      }
    } catch { toast.error('Validation failed'); }
    setValidatingPromo(false);
  };

  // Staff: apply promo/referral code to invoice or estimate
  const handleStaffApplyCode = async () => {
    const code = staffPromoInput.trim().toUpperCase();
    if (!code) return;
    setStaffApplyingCode(true);
    try {
      const url = isInvoice ? `${API}/invoices/${doc.id}/apply-code` : `${API}/estimates/${doc.id}/apply-code`;
      const { data } = await axios.post(url, { code }, { withCredentials: true });
      toast.success(`Code ${data.code} applied — ${data.discount_amount ? `-$${Number(data.discount_amount).toFixed(2)}` : 'discount'}`);
      setStaffPromoInput('');
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Apply failed'); }
    setStaffApplyingCode(false);
  };

  const handleStaffRemoveCode = async () => {
    try {
      await axios.post(`${API}/invoices/${doc.id}/remove-code`, {}, { withCredentials: true });
      toast.success('Code removed');
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Remove failed'); }
  };

  // iter197 — Payment Correction: open modal pre-filled with current values.
  const openCorrectPayment = () => {
    setCorrAmount(String(doc.amount_paid ?? 0));
    setCorrOnSite(doc.amount_collected_on_site == null ? '' : String(doc.amount_collected_on_site));
    setCorrDisposition(doc.payment_disposition_key || '');
    setCorrMethod(doc.payment_method || '');
    setCorrMethodDetail(doc.payment_method_detail || '');
    setCorrReference(doc.payment_reference || '');
    setCorrNotes(doc.payment_notes || '');
    setCorrDate('');
    setCorrStatus('');
    setCorrReason('');
    setCorrReasonNote('');
    setShowCorrectPayment(true);
  };

  const handleCorrectPayment = async () => {
    if (!corrReason) { toast.error('Select a correction reason'); return; }
    if (corrAmount === '' || isNaN(Number(corrAmount))) { toast.error('Enter a corrected amount paid'); return; }
    setCorrSaving(true);
    try {
      const body = {
        amount_paid: Number(corrAmount),
        amount_collected_on_site: corrOnSite === '' ? null : Number(corrOnSite),
        disposition: corrDisposition || null,
        payment_method: corrMethod || null,
        payment_method_detail: corrMethodDetail || null,
        payment_reference: corrReference,
        payment_notes: corrNotes,
        paid_date: corrDate || null,
        status: corrStatus || null,
        correction_reason: corrReason,
        reason_note: corrReasonNote,
      };
      const { data } = await axios.post(`${API}/invoices/${doc.id}/correct-payment`, body, { withCredentials: true });
      toast.success(`Payment corrected · ${data.status.replace('_', ' ').toUpperCase()} · Balance $${Number(data.balance_due).toFixed(2)}`);
      setShowCorrectPayment(false);
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Correction failed'); }
    setCorrSaving(false);
  };

  // Staff: mark invoice paid in person
  const handleStaffMarkPaid = async () => {
    try {
      const body = {
        payment_method: markPaidMethod,
        payment_source: markPaidSource,
        reference: markPaidRef,
        notes: markPaidNotes,
        // iter140 — Phase 5: explicit opt-out for residential receipt auto-send.
        send_receipt: !!markPaidSendReceipt,
      };
      if (markPaidAmount) body.amount_paid = Number(markPaidAmount);
      if (markPaidDate) body.paid_date = markPaidDate;
      const { data } = await axios.post(`${API}/invoices/${doc.id}/mark-paid`, body, { withCredentials: true });
      // iter140 — surface receipt result so staff knows what happened.
      const receipt = data?.receipt;
      if (receipt?.sent) {
        toast.success(`Marked paid · Receipt emailed to ${receipt.to}`);
      } else if (receipt?.reason && !['opted_out_by_staff', 'not_residential', 'no_customer_email', 'invoice_not_fully_paid'].includes(receipt.reason)) {
        toast.warning(`Marked paid, but receipt was not sent (${receipt.reason}).`);
      } else if (receipt?.reason === 'no_customer_email') {
        toast.success('Marked paid · No customer email on file — receipt skipped.');
      } else {
        toast.success('Invoice marked paid');
      }
      onChange?.();
      fetchInvoicePayments();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Staff: refund
  const handleStaffRefund = async () => {
    if (!refundReason.trim()) { toast.error('Reason required'); return; }
    try {
      const body = { reason: refundReason };
      if (refundAmount) body.amount = Number(refundAmount);
      await axios.post(`${API}/invoices/${doc.id}/refund`, body, { withCredentials: true });
      toast.success('Refund recorded');
      setShowRefund(false);
      onChange?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // iter134 — Phase 2: advance HSP status (e.g. pending_office_review → submitted_to_hsp).
  const handleHspStatusChange = async (newStatus) => {
    if (!isWarrantyInvoice) return;
    setHspSavingStatus(true);
    try {
      await axios.post(`${API}/invoices/${doc.id}/hsp/update-status`,
        { hsp_review_status: newStatus }, { withCredentials: true });
      toast.success(`Status: ${HSP_STATUS_LABEL[newStatus] || newStatus}`);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update HSP status');
    }
    setHspSavingStatus(false);
  };

  // iter134 — Phase 2: record HSP/warranty payment received.
  const handleHspMarkPaid = async () => {
    if (!isWarrantyInvoice) return;
    const amt = Number(hspAmount);
    if (!amt || amt <= 0) { toast.error('Amount received is required'); return; }
    if ((hspMethod === 'check' || hspMethod === 'ach' || hspMethod === 'wire') && !hspReference.trim()) {
      toast.error(hspMethod === 'check' ? 'Check # required' : 'Reference / transaction # required');
      return;
    }
    setHspSavingPaid(true);
    try {
      const body = {
        amount_received: amt,
        method: hspMethod,
        reference: hspReference.trim(),
        notes: hspNotes.trim(),
      };
      if (hspDate) body.payment_date = hspDate;
      const { data } = await axios.post(`${API}/invoices/${doc.id}/hsp/mark-paid`, body, { withCredentials: true });
      toast.success(data.fully_paid ? 'HSP marked fully paid' : `HSP partial payment recorded ($${amt.toFixed(2)})`);
      setShowHspPaid(false);
      onChange?.();
      if (data.fully_paid) onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record HSP payment');
    }
    setHspSavingPaid(false);
  };

  // iter136 — Phase 3: change Office Billing (QuickBooks) Status with optional note.
  const handleOfficeBillingStatusChange = async (nextStatus) => {
    if (!canManageOfficeBilling) return;
    if (nextStatus === officeBillingStatus && !officeBillingNote.trim()) {
      toast.message('No change');
      return;
    }
    setOfficeBillingSaving(true);
    try {
      await axios.post(`${API}/invoices/${doc.id}/office-status`,
        { office_billing_status: nextStatus, note: officeBillingNote.trim() },
        { withCredentials: true });
      const label = (OFFICE_BILLING_OPTIONS.find((o) => o.key === nextStatus)?.label) || nextStatus;
      toast.success(`QB Status → ${label}`);
      setOfficeBillingNote('');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update QB status');
    }
    setOfficeBillingSaving(false);
  };

  // Staff: get/copy payment link
  const handleGetPaymentLink = async () => {
    try {
      const { data } = await axios.get(`${API}/invoices/${doc.id}/payment-link`, { withCredentials: true });
      setPaymentLink(data.link);
      try { await navigator.clipboard.writeText(data.link); toast.success('Payment link copied'); }
      catch { toast.success('Payment link ready'); }
    } catch { toast.error('Failed'); }
  };

  // Staff: open Stripe Checkout on this device to charge the customer's card right now.
  // Opens /pay/{token} in a new tab; this admin view will auto-refresh via parent polling once paid.
  const handleChargeCardNow = async () => {
    try {
      const { data } = await axios.get(`${API}/invoices/${doc.id}/payment-link`, { withCredentials: true });
      const win = window.open(data.link, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Popup blocked — allow popups, or use the Copy Payment Link button instead');
        return;
      }
      toast.success('Card entry opened in new tab. This view will update once payment completes.');
      // Poll our own backend for invoice status every 3s; close out when paid.
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const { data: list } = await axios.get(`${API}/invoices/all`, { withCredentials: true });
          const updated = list.find(i => i.id === doc.id);
          if (updated && (updated.status === 'paid' || updated.status === 'refunded' || updated.status === 'partially_refunded')) {
            clearInterval(poll);
            toast.success('Payment received!');
            onChange?.();
            onOpenChange(false);
          }
        } catch { /* ignore */ }
        if (attempts >= 60) clearInterval(poll); // 3 min max
      }, 3000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start card entry'); }
  };

  // Customer pays invoice via Stripe
  const handlePayInvoice = async (paymentType) => {    setPaying(true);
    try {
      const body = { payment_type: paymentType, origin_url: window.location.origin };
      if (!memberDiscountActive && promoApplied?.code) body.promo_code = promoApplied.code;
      const { data } = await axios.post(`${API}/invoices/${doc.id}/checkout`, body, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setPaying(false);
    }
  };

  // Show customer pay buttons? (Customer controls ONLY appear after office release — iter217.)
  const released = doc?.customer_release_status === 'approved_for_customer' && doc?.customer_visible;
  const showCustomerPay = viewerRole === 'customer' && isInvoice && released && ['accepted', 'deposit_paid'].includes(status) && remainingBalance > 0;
  const showCustomerAcceptDecline = viewerRole === 'customer' && isInvoice && released && status === 'pending';
  const showPromoSection = viewerRole === 'customer' && isInvoice && released && ['pending', 'accepted', 'deposit_paid'].includes(status) && remainingBalance > 0;
  const showCustomerEstimateActions = viewerRole === 'customer' && !isInvoice && released && status === 'pending';

  return (
    <>
      {createPortal(
        <div id="print-portal-doc" style={{ display: 'none' }}>
          <PrintableDoc doc={{ ...doc, line_items: lineItems, notes, deposit_amount: depositAmount }} type={type} computedSubtotal={subtotal} remainingBalance={remainingBalance} discountAmount={discountAmount} discountSource={discountSource} discountPercent={effectiveDiscountPercent} />
        </div>,
        document.body
      )}
      {/* iter138 — Phase 4: Full Office Packet view (printable). Toggled via "Print Full Packet". */}
      <PrintablePacket doc={{ ...doc, line_items: lineItems, notes, deposit_amount: depositAmount }} type={type} computedSubtotal={subtotal} remainingBalance={remainingBalance} discountAmount={discountAmount} discountSource={discountSource} discountPercent={effectiveDiscountPercent} />
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto" data-testid="billing-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center justify-between pr-8">
              <div className="flex items-center gap-2 flex-wrap">
                {editingNumber ? (
                  // iter109 — Inline editor (admin/SM/super only for estimates).
                  <span className="flex items-center gap-2" data-testid="estimate-number-editor">
                    <Input
                      data-testid="estimate-number-input"
                      autoFocus
                      value={numberDraft}
                      onChange={(e) => setNumberDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleSaveNumber(); }
                        if (e.key === 'Escape') {
                          // iter109 — Stop Escape from bubbling to the Radix Sheet's
                          // onEscapeKeyDown (which would close the whole detail sheet).
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingNumber(false);
                          setNumberDraft(doc.estimate_number || '');
                        }
                      }}
                      placeholder="P25181"
                      className="bg-[#0A0A0A] border-[#262626] text-white text-2xl h-10 w-44 px-3"
                      disabled={savingNumber}
                    />
                    <Button
                      data-testid="estimate-number-save"
                      onClick={handleSaveNumber}
                      disabled={savingNumber}
                      size="sm"
                      className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-9"
                    >
                      {savingNumber ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                    <Button
                      data-testid="estimate-number-cancel"
                      onClick={() => { setEditingNumber(false); setNumberDraft(doc.estimate_number || ''); }}
                      variant="outline"
                      size="sm"
                      disabled={savingNumber}
                      className="border-[#262626] text-white hover:bg-[#262626] h-9"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="text-2xl" data-testid="billing-number-display">{number}</span>
                    {canEditEstimateNumber && (
                      <button
                        data-testid="estimate-number-edit-btn"
                        onClick={() => { setNumberDraft(doc.estimate_number || ''); setEditingNumber(true); }}
                        title="Edit estimate number (e.g. to match QuickBooks)"
                        className="p-1.5 rounded hover:bg-[#262626] text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </>
                )}
                <StatusBadge status={status} />
                {/* iter165 — Payment method badge next to PAID so admins see at a glance how it was paid. */}
                <PaymentMethodBadge method={doc.payment_method} status={status} />
                {doc.from_estimate_number && <span className="text-xs text-[#A3A3A3] italic">from {doc.from_estimate_number}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="billing-print-button" onClick={handlePrint} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]">
                  <Printer className="w-4 h-4 mr-2" /> {isInvoice ? 'Print Customer Invoice' : 'Print'}
                </Button>
                <Button data-testid="billing-print-packet-button" onClick={handlePrintPacket} variant="outline" size="sm" className="border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20">
                  <Printer className="w-4 h-4 mr-2" /> {isInvoice ? 'Print Office Packet' : 'Print Full Packet'}
                </Button>
              </div>
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-4 mt-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer</p>
                <p className="text-white font-medium">{doc.customer_name}</p>
              </div>
              <div>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Date</p>
                <p className="text-white font-medium">{fmtLocalDate(doc.service_date || doc.date)}</p>
              </div>
            </div>

            {editing && <DraftStatus status={editDraft.status} restoredAt={editDraft.restoredAt} testIdPrefix="detail-edit-draft" />}
            <div className="border-t border-[#262626] pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider font-bold">Line Items</p>
                {editing && <Button data-testid="add-line-item" onClick={addLine} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-7"><Plus className="w-3 h-3 mr-1" /> Add</Button>}
              </div>

              <div className="space-y-2">
                {lineItems.map((li, idx) => (
                  <div key={li._key} data-testid={`line-item-${idx}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2 space-y-1.5">
                    <div className="grid grid-cols-[1fr_70px_100px_100px_auto] gap-2 items-center">
                    {editing ? (
                      <>
                        <Input value={li.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} placeholder="Description" className="bg-[#141414] border-[#262626] text-white h-8 text-sm" />
                        <Input
                          type="number" min="0" step="0.5"
                          placeholder="1"
                          /* iter129 — Show '' for 0 + onFocus select so the field never refuses input. */
                          value={(li.quantity === 0 || li.quantity === '' || li.quantity == null) ? '' : String(li.quantity)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value === '' ? 0 : Number(e.target.value))}
                          className="bg-[#141414] border-[#262626] text-white h-8 text-sm text-right"
                        />
                        <Input
                          type="number" min="0" step="0.01"
                          placeholder="0.00"
                          value={(li.unit_price === 0 || li.unit_price === '' || li.unit_price == null) ? '' : String(li.unit_price)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => updateLine(idx, 'unit_price', e.target.value === '' ? 0 : Number(e.target.value))}
                          className="bg-[#141414] border-[#262626] text-white h-8 text-sm text-right"
                        />
                        <div className="text-[#D4AF37] font-bold text-right text-sm">${computeLineTotal(li).toFixed(2)}</div>
                        {lineItems.length > 1 && <Button onClick={() => removeLine(idx)} size="sm" variant="ghost" className="text-red-400 hover:bg-[#7F1D1D] h-8 w-8 p-0"><Trash2 className="w-3 h-3" /></Button>}
                      </>
                    ) : (
                      <>
                        <div className="text-white text-sm">{li.description || <span className="italic text-[#525252]">No description</span>}</div>
                        <div className="text-[#A3A3A3] text-sm text-right">{li.quantity}</div>
                        <div className="text-[#A3A3A3] text-sm text-right">${Number(li.unit_price).toFixed(2)}</div>
                        <div className="text-[#D4AF37] font-bold text-right text-sm">${computeLineTotal(li).toFixed(2)}</div>
                        <div />
                      </>
                    )}
                    </div>
                    {/* iter150 — Per-line Kind selector when editing. Read-only kind chip when viewing. */}
                    {editing ? (
                      <div className="grid grid-cols-[2fr_auto] gap-2 items-center">
                        <select
                          data-testid={`detail-line-kind-${idx}`}
                          value={li.kind || 'service'}
                          onChange={(e) => updateLine(idx, 'kind', e.target.value)}
                          className="bg-[#141414] border border-[#262626] text-white text-xs rounded h-7 px-2"
                        >
                          <option value="service">Service · 10% member discount eligible</option>
                          <option value="membership">Membership · activates on paid</option>
                          <option value="credit">Credit · negative line, never re-discounted</option>
                          <option value="fee">Fee · permits/trip/financing</option>
                          <option value="tax">Tax · never discounted</option>
                          <option value="membership_discount">Membership Discount · auto-generated</option>
                        </select>
                        <label className="text-[10px] text-[#A3A3A3] flex items-center gap-1.5 cursor-pointer select-none px-2 whitespace-nowrap">
                          <input
                            data-testid={`detail-line-warranty-${idx}`}
                            type="checkbox"
                            checked={!!li.warranty_covered}
                            onChange={(e) => updateLine(idx, 'warranty_covered', e.target.checked)}
                            className="w-3 h-3 accent-purple-500"
                          />
                          HSP/Warranty
                        </label>
                      </div>
                    ) : (
                      (li.kind && li.kind !== 'service') || li.warranty_covered ? (
                        <div className="flex flex-wrap gap-1.5 text-[10px]">
                          {li.kind && li.kind !== 'service' && (
                            <span data-testid={`detail-line-kind-chip-${idx}`} className="bg-[#262626] text-[#D4AF37] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">{li.kind.replace('_', ' ')}</span>
                          )}
                          {li.warranty_covered && (
                            <span className="bg-purple-500/20 border border-purple-500/40 text-purple-300 px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">HSP / Warranty</span>
                          )}
                        </div>
                      ) : null
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-3 flex justify-end">
                <div className="w-72 space-y-1 text-sm">
                  <div className="flex justify-between text-[#A3A3A3]"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  {effectiveDiscountPercent > 0 && (
                    <div className="flex justify-between text-green-400" data-testid="discount-row">
                      <span>{memberDiscountActive ? 'Membership Savings' : 'Promo Savings'} ({effectiveDiscountPercent}%)</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {deposit > 0 && <div className="flex justify-between text-[#A3A3A3]"><span>Deposit Required</span><span>${discountedDeposit.toFixed(2)}</span></div>}
                  {amountPaid > 0 && <div className="flex justify-between text-green-400"><span>Amount Paid</span><span>-${amountPaid.toFixed(2)}</span></div>}
                  {/* iter105 — Refund summary line, shown when Stripe (or staff) refunded any portion of this invoice. */}
                  {(doc.refund_amount || 0) > 0 && (
                    <div data-testid="invoice-refund-summary" className="flex justify-between text-purple-300">
                      <span>Refunded{doc.refund_source === 'stripe' ? ' (Stripe)' : ''}</span>
                      <span>-${Number(doc.refund_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-[#D4AF37]">
                    <span>{amountPaid > 0 ? 'Balance' : 'Total'}</span>
                    <span className="text-[#D4AF37]">${remainingBalance.toFixed(2)}</span>
                  </div>
                  {discountSource && (
                    <p className="text-[10px] text-[#525252] text-right uppercase tracking-wider mt-1">Discount: {discountSource}</p>
                  )}
                </div>
              </div>

              {/* iter231 — Payment integrity: on-site vs total paid vs balance are SEPARATE. */}
              <div data-testid="payment-integrity-block" className="mt-3 rounded-lg border border-[#262626] bg-[#0A0A0A] p-3">
                <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mb-2 font-bold">Payment Integrity</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div><p className="text-[10px] uppercase text-[#A3A3A3]">Invoice Total</p><p className="text-white font-bold" data-testid="pi-total">${Number(doc.amount || 0).toFixed(2)}</p></div>
                  <div><p className="text-[10px] uppercase text-[#A3A3A3]">Collected On Site</p><p className="text-white font-bold" data-testid="pi-onsite">{doc.amount_collected_on_site == null ? <span className="text-orange-300 text-xs">Unknown — Legacy</span> : `$${Number(doc.amount_collected_on_site).toFixed(2)}`}</p></div>
                  <div><p className="text-[10px] uppercase text-[#A3A3A3]">Total Paid</p><p className="text-white font-bold" data-testid="pi-totalpaid">${Number(doc.amount_paid || 0).toFixed(2)}</p></div>
                  <div><p className="text-[10px] uppercase text-[#A3A3A3]">Balance Due</p><p className="text-white font-bold" data-testid="pi-balance">${(Number(doc.amount || 0) - Number(doc.amount_paid || 0)).toFixed(2)}</p></div>
                  {doc.payment_disposition_key && <div><p className="text-[10px] uppercase text-[#A3A3A3]">Disposition</p><p className="text-white text-xs" data-testid="pi-disposition">{DISPOSITION_LABELS_UI[doc.payment_disposition_key] || doc.payment_disposition_key}</p></div>}
                  <div><p className="text-[10px] uppercase text-[#A3A3A3]">Payment Method</p><p className="text-white text-xs">{formatPaymentMethod(doc.payment_method, doc.payment_method_detail) || '—'}</p></div>
                  <div><p className="text-[10px] uppercase text-[#A3A3A3]">QuickBooks Status</p><p className="text-white text-xs">{(doc.office_billing_status || 'needs_review').replace(/_/g, ' ')}</p></div>
                </div>
                {/* PO block */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mt-3 pt-2 border-t border-[#1f1f1f]">
                  {doc.work_order_number ? <div><p className="text-[10px] uppercase text-[#A3A3A3]">Work Order #</p><p className="text-white text-xs" data-testid="pi-wo">{doc.work_order_number}</p></div> : null}
                  {doc.office_supplied_po ? <div><p className="text-[10px] uppercase text-[#A3A3A3]">Office-Supplied PO</p><p className="text-white text-xs" data-testid="pi-office-po">{doc.office_supplied_po}</p></div> : null}
                  {doc.tech_supplied_po ? <div><p className="text-[10px] uppercase text-[#A3A3A3]">Tech-Supplied PO</p><p className="text-white text-xs" data-testid="pi-tech-po">{doc.tech_supplied_po}</p></div> : null}
                  {doc.final_billing_po ? <div><p className="text-[10px] uppercase text-[#A3A3A3]">Final Billing PO</p><p className="text-white text-xs" data-testid="pi-final-po">{doc.final_billing_po}</p></div> : null}
                </div>
                {(doc.office_supplied_po || '').trim() && (doc.tech_supplied_po || '').trim() && doc.office_supplied_po.trim().toLowerCase() !== doc.tech_supplied_po.trim().toLowerCase() && (
                  <p className="mt-2 text-[11px] font-bold text-orange-300" data-testid="pi-po-review">⚠ PO Review Needed — office and technician PO numbers differ</p>
                )}
              </div>
            </div>

            <div className="border-t border-[#262626] pt-4">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Notes</Label>
              {editing ? (
                <Textarea data-testid="detail-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              ) : (
                <p className="text-white text-sm whitespace-pre-wrap mt-1">{notes || <span className="italic text-[#525252]">No notes</span>}</p>
              )}
            </div>

            {canEdit && editing && (
              <div className="border-t border-[#262626] pt-4 space-y-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Deposit Required ($)</Label>
                  <Input data-testid="detail-deposit" type="number" min="0" step="0.01" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="0 = no deposit" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                  <p className="text-[10px] text-[#525252] mt-1">Leave 0 to require pay-in-full only.</p>
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="detail-status" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {isInvoice ? (
                        <>
                          <SelectItem value="pending" className="text-white">Pending</SelectItem>
                          <SelectItem value="accepted" className="text-white">Accepted</SelectItem>
                          <SelectItem value="declined" className="text-white">Declined</SelectItem>
                          <SelectItem value="deposit_paid" className="text-white">Deposit Paid</SelectItem>
                          <SelectItem value="paid" className="text-white">Paid</SelectItem>
                          <SelectItem value="overdue" className="text-white">Overdue</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="pending" className="text-white">Pending</SelectItem>
                          <SelectItem value="approved" className="text-white">Approved</SelectItem>
                          <SelectItem value="rejected" className="text-white">Rejected</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Promo code / membership discount section (visible from pending state) */}
            {showPromoSection && (
              <div className="border-t border-[#262626] pt-4">
                {!memberDiscountActive && (
                  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
                    <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">Have a Promo Code?</p>
                    {promoApplied ? (
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <span className="text-[#D4AF37] font-mono font-bold">{promoApplied.code}</span>
                          <span className="text-green-400 text-xs ml-2">— {promoApplied.discount_percent}% applied</span>
                          {promoApplied.vip_name && <p className="text-[10px] text-[#525252]">Referred by {promoApplied.vip_name}</p>}
                        </div>
                        <Button data-testid="remove-promo" onClick={() => setPromoApplied(null)} variant="outline" size="sm" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white">Remove</Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input data-testid="promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Enter code" className="bg-[#141414] border-[#262626] text-white uppercase font-mono" />
                        <Button data-testid="apply-promo" onClick={handleValidatePromo} disabled={validatingPromo || !promoInput.trim()} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                          {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {memberDiscountActive && (
                  <div className="bg-green-500/5 border border-green-700/40 rounded-md p-3">
                    <p className="text-green-400 text-xs font-bold uppercase tracking-wider">✓ Member Discount Active</p>
                    <p className="text-[#A3A3A3] text-xs mt-1">Your 10% membership discount is automatically applied. Promo codes cannot be combined.</p>
                  </div>
                )}
              </div>
            )}

            {/* Customer: accept/decline invoice */}
            {showCustomerAcceptDecline && (
              <div className="flex gap-2">
                <Button data-testid="customer-accept-invoice" onClick={() => handleInvoiceResponse('accept')} className="bg-green-600 hover:bg-green-700 text-white flex-1"><CheckCircle className="w-4 h-4 mr-2" /> Accept Invoice</Button>
                <Button data-testid="customer-decline-invoice" onClick={() => handleInvoiceResponse('decline')} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white flex-1"><XCircle className="w-4 h-4 mr-2" /> Decline</Button>
              </div>
            )}

            {/* Customer: pay invoice */}
            {showCustomerPay && (
              <div className="border-t border-[#262626] pt-4 space-y-3">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Pay Online</p>
                <div className="flex gap-2 flex-wrap">
                  {deposit > 0 && status === 'accepted' && remainingDeposit > 0 && (
                    <Button data-testid="customer-pay-deposit" onClick={() => handlePayInvoice('deposit')} disabled={paying} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1">
                      {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                      Pay Deposit ${remainingDeposit.toFixed(2)}
                    </Button>
                  )}
                  <Button data-testid="customer-pay-full" onClick={() => handlePayInvoice('full')} disabled={paying} className="bg-green-600 hover:bg-green-700 text-white flex-1">
                    {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Pay {amountPaid > 0 ? 'Balance' : 'in Full'} ${remainingBalance.toFixed(2)}
                  </Button>
                </div>
                <p className="text-[10px] text-[#525252]">Secure payment via Stripe.</p>
              </div>
            )}

            {/* Customer: estimate approve/reject */}
            {showCustomerEstimateActions && (
              <div className="border-t border-[#262626] pt-4 flex gap-2">
                <Button data-testid="customer-approve" onClick={() => handleEstimateStatus('approved')} className="bg-green-600 hover:bg-green-700 text-white flex-1"><CheckCircle className="w-4 h-4 mr-2" /> Approve</Button>
                <Button data-testid="customer-reject" onClick={() => handleEstimateStatus('rejected')} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white flex-1"><XCircle className="w-4 h-4 mr-2" /> Reject</Button>
              </div>
            )}

            {viewerRole === 'customer' && !isInvoice && status === 'approved' && (
              <div className="border-t border-[#262626] pt-4 bg-green-500/5 -mx-6 px-6 py-3">
                <p className="text-green-400 text-sm font-medium">✓ Estimate approved — invoice has been created. Check your Invoices tab to pay.</p>
              </div>
            )}

            {/* ─────────── STAFF ACTIONS ─────────── */}
            {viewerRole !== 'customer' && (
              <div data-testid="staff-actions-panel" className="border-t border-[#262626] pt-4 space-y-3">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Staff Actions</p>

                {/* iter217 — Customer-release gate. Nothing is visible to the customer
                    until the office explicitly approves & releases it. */}
                {(() => {
                  const rs = doc?.customer_release_status;
                  const internalOnly = doc?.internal_only || doc?.payor_kind === 'warranty' || doc?.payor_kind === 'hsp';
                  const amtPending = doc?.amount_pending || (!isInvoice && (Number(doc?.amount) || 0) <= 0);
                  const label = {
                    approved_for_customer: 'Approved & Released to Customer',
                    needs_office_review: 'Needs Office Review — Not Visible to Customer',
                    returned_for_correction: 'Returned for Correction — Not Visible',
                    on_hold: 'On Hold — Not Visible',
                    withdrawn: 'Withdrawn — Not Visible',
                    internal_draft: 'Internal Only — Not Visible',
                  }[rs] || 'Needs Office Review — Not Visible to Customer';
                  const isReleased = rs === 'approved_for_customer';
                  return (
                    <div data-testid="release-gate-panel" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs uppercase tracking-wider text-[#A3A3A3]">Customer Visibility</span>
                        <span data-testid="release-status-badge" className={`text-[11px] font-bold px-2 py-0.5 rounded ${isReleased ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-300'}`}>
                          {isReleased ? 'VISIBLE' : 'HIDDEN'}
                        </span>
                      </div>
                      <p className="text-white text-sm">{label}</p>
                      {doc?.customer_approved_by_name && isReleased && (
                        <p className="text-[11px] text-[#525252]">Released by {doc.customer_approved_by_name}</p>
                      )}
                      {internalOnly ? (
                        <p className="text-[11px] text-red-300">Internal warranty/HSP record — cannot be released to the customer.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {!isReleased && (
                            <Button
                              data-testid="release-approve-btn"
                              onClick={() => handleRelease('approve')}
                              disabled={amtPending}
                              className="bg-green-600 hover:bg-green-700 text-white h-9 disabled:opacity-40"
                              title={amtPending ? 'Add valid pricing before releasing' : ''}
                            >
                              <CheckCircle className="w-4 h-4 mr-1.5" /> Approve &amp; Release to Customer
                            </Button>
                          )}
                          {isReleased && (
                            <Button data-testid="release-withdraw-btn" onClick={() => handleRelease('withdraw')} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white h-9">
                              <XCircle className="w-4 h-4 mr-1.5" /> Withdraw From Portal
                            </Button>
                          )}
                          {!isReleased && (
                            <>
                              <Button data-testid="release-return-btn" onClick={() => handleRelease('return')} variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white h-9">Return for Correction</Button>
                              <Button data-testid="release-hold-btn" onClick={() => handleRelease('hold')} variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white h-9">Put On Hold</Button>
                            </>
                          )}
                        </div>
                      )}
                      {amtPending && !internalOnly && !isReleased && (
                        <p className="text-[11px] text-amber-300">Amount Pending — add valid pricing before releasing.</p>
                      )}
                    </div>
                  );
                })()}

                {/* iter116 — Correct Completion entry point (admin/super_admin/SM only).
                    Shows when this invoice was generated from a job completion so admins
                    can fix tech mistakes (payment method, checklist, etc.) without a developer. */}
                {isInvoice && doc.completion_id && canCorrectCompletion(user) && (
                  <Button
                    data-testid="staff-correct-completion"
                    onClick={() => setEditingCompletionId(doc.completion_id)}
                    variant="outline"
                    className="w-full h-11 border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 font-bold"
                  >
                    <Edit2 className="w-4 h-4 mr-2" /> Correct Completion
                  </Button>
                )}

                {/* iter197 — Correct Payment (admin/office with edit_invoices).
                    Safe path to fix a wrong paid amount / method / reference
                    without deleting the invoice or breaking the completion. */}
                {isInvoice && hasPermission('edit_invoices') && (
                  <Button
                    data-testid="correct-payment-open"
                    onClick={openCorrectPayment}
                    variant="outline"
                    className="w-full h-11 border-blue-500/50 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 font-bold"
                  >
                    <Wallet className="w-4 h-4 mr-2" /> Correct Payment
                  </Button>
                )}

                {/* iter197 — Original Tech Closeout vs Office Correction audit. */}
                {isInvoice && (doc.original_closeout || (Array.isArray(doc.payment_corrections) && doc.payment_corrections.length > 0)) && (
                  <div data-testid="payment-correction-history" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-3">
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
                      <History className="w-3.5 h-3.5" /> Payment Correction History
                    </p>
                    {doc.original_closeout && (
                      <div className="border-l-2 border-[#525252] pl-3">
                        <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">Original Tech Closeout</p>
                        {doc.original_closeout.entered_by_name && (
                          <p className="text-xs text-[#737373]">Entered by {doc.original_closeout.entered_by_name}</p>
                        )}
                        <p className="text-sm text-white mt-1">Amount: ${Number(doc.original_closeout.amount_paid || 0).toFixed(2)}</p>
                        {doc.original_closeout.payment_method && <p className="text-xs text-[#A3A3A3]">Method: {formatPaymentMethod(doc.original_closeout.payment_method, doc.original_closeout.payment_method_detail)}</p>}
                        {doc.original_closeout.payment_reference && <p className="text-xs text-[#A3A3A3]">Ref: {doc.original_closeout.payment_reference}</p>}
                        {doc.original_closeout.payment_notes && <p className="text-xs text-[#A3A3A3] italic">"{doc.original_closeout.payment_notes}"</p>}
                      </div>
                    )}
                    {Array.isArray(doc.payment_corrections) && doc.payment_corrections.map((c, i) => (
                      <div key={i} className="border-l-2 border-blue-500/60 pl-3" data-testid={`payment-correction-${i}`}>
                        <p className="text-[10px] uppercase tracking-wider text-blue-300 font-bold">Office Correction</p>
                        <p className="text-sm text-white mt-1">${Number(c.original_amount_paid || 0).toFixed(2)} → <span className="text-blue-300 font-bold">${Number(c.corrected_amount_paid || 0).toFixed(2)}</span></p>
                        {c.corrected_payment_method && <p className="text-xs text-[#A3A3A3]">Method: {formatPaymentMethod(c.corrected_payment_method, c.corrected_payment_method_detail)}</p>}
                        {c.corrected_reference && <p className="text-xs text-[#A3A3A3]">Ref: {c.corrected_reference}</p>}
                        <p className="text-xs text-[#A3A3A3]">Reason: {CORRECTION_REASON_LABEL[c.correction_reason] || c.correction_reason}{c.reason_note ? ` — ${c.reason_note}` : ''}</p>
                        <p className="text-[10px] text-[#737373] mt-0.5">By {c.corrected_by_name} · {c.corrected_at ? fmtLocalDate(c.corrected_at) : ''}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* iter195 — Field Completion Details (read-only) so office
                    always sees everything the tech entered — tech notes,
                    recommended follow-ups, payment notes, HSP notes, photos,
                    checklist. Rendered here so it's visible to viewers who
                    don't have the Correct Completion permission. */}
                {isInvoice && doc.completion_id && (
                  <FieldCompletionDetails completionId={doc.completion_id} />
                )}

                {/* iter310 — Billing Follow-Up (why revisit / when / who). Separate from QB Status. */}
                {isInvoice && canManageOfficeBilling && (
                  <div data-testid="invoice-followup-panel" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[#A3A3A3] text-xs uppercase tracking-wider font-bold">Billing Follow-Up</p>
                      {doc.billing_followup && !doc.billing_followup.resolved
                        ? <div className="mt-1"><FollowUpChip followup={doc.billing_followup} testid="invoice-followup-chip" onClick={() => setFollowupOpen(true)} />{doc.billing_followup.note && <p className="text-[11px] text-[#A3A3A3] mt-1">{doc.billing_followup.note}</p>}</div>
                        : <p className="text-[11px] text-[#525252] mt-0.5">No follow-up scheduled.</p>}
                    </div>
                    <Button data-testid="invoice-followup-btn" size="sm" variant="outline" onClick={() => setFollowupOpen(true)} className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 text-xs">
                      {doc.billing_followup && !doc.billing_followup.resolved ? 'Edit Follow-Up' : 'Add Reminder / Follow Up'}
                    </Button>
                    <BillingFollowUpDialog
                      open={followupOpen}
                      onOpenChange={setFollowupOpen}
                      recordType="invoice"
                      recordId={doc.id}
                      followup={doc.billing_followup}
                      title={`${doc.customer_name || ''} · ${doc.invoice_number || ''}`}
                      onSaved={() => onChange?.()}
                    />
                  </div>
                )}

                {/* iter136 — Phase 3: Office Billing (QuickBooks) Status tracker.
                    Internal workflow only — QuickBooks remains the official ledger. */}
                {isInvoice && canManageOfficeBilling && (
                  <div data-testid="office-billing-panel" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-[#A3A3A3] text-xs uppercase tracking-wider font-bold">Office Billing (QuickBooks) Status</p>
                      <span
                        data-testid="office-billing-status-badge"
                        className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${officeBillingMeta.color}`}
                      >
                        {officeBillingMeta.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Select
                        value={officeBillingStatus}
                        onValueChange={(v) => handleOfficeBillingStatusChange(v)}
                      >
                        <SelectTrigger data-testid="office-billing-select" className="bg-[#141414] border-[#262626] text-white h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          {OFFICE_BILLING_OPTIONS.map((o) => (
                            <SelectItem key={o.key} value={o.key} className="text-white">{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {officeBillingSaving && <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37] self-center" />}
                    </div>
                    <Input
                      data-testid="office-billing-note"
                      value={officeBillingNote}
                      onChange={(e) => setOfficeBillingNote(e.target.value)}
                      placeholder="Optional note (e.g. QB-2026-01, called HSP)"
                      className="bg-[#141414] border-[#262626] text-white h-8 text-xs"
                    />
                    <p className="text-[10px] text-[#525252]">
                      Internal workflow tracker only — QuickBooks remains the official accounting source.
                    </p>
                    {/* iter258 — QuickBooks Invoice # (external accounting reference).
                        Optional, office/admin editable, distinct from the NexFlow invoice #. */}
                    <div data-testid="qb-invoice-number-block" className="pt-2 border-t border-[#262626] space-y-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">QuickBooks Invoice #</p>
                      {doc.quickbooks_invoice_number && (
                        <div data-testid="qb-invoice-number-current" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#1E3A2F] border border-[#2E7D5B]/60">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-[#3FA877]">QB Invoice</span>
                          <span className="text-base font-bold text-[#5FD69B] leading-none">#{doc.quickbooks_invoice_number}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-[1fr_auto] gap-2">
                        <Input
                          data-testid="qb-invoice-number-input"
                          value={qbInvoiceNumber}
                          onChange={(e) => setQbInvoiceNumber(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveQbNumber(); } }}
                          placeholder="e.g. 10432 (from QuickBooks)"
                          className="bg-[#141414] border-[#262626] text-white h-8 text-xs"
                        />
                        <Button
                          data-testid="qb-invoice-number-save"
                          onClick={handleSaveQbNumber}
                          disabled={qbSaving || qbInvoiceNumber.trim() === (doc.quickbooks_invoice_number || '')}
                          size="sm"
                          className="h-8 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-40"
                        >
                          {qbSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                      <p className="text-[10px] text-[#525252]">
                        Optional cross-reference to the matching invoice in QuickBooks. Searchable from the invoice list.
                      </p>
                    </div>
                    {officeBillingHistory.length > 0 && (
                      <div>
                        <button
                          type="button"
                          data-testid="office-billing-history-toggle"
                          onClick={() => setOfficeBillingHistoryOpen((v) => !v)}
                          className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:underline"
                        >
                          {officeBillingHistoryOpen ? 'Hide' : 'Show'} history ({officeBillingHistory.length})
                        </button>
                        {officeBillingHistoryOpen && (
                          <div data-testid="office-billing-history" className="mt-2 space-y-1 border-t border-[#262626] pt-2">
                            {[...officeBillingHistory].reverse().map((h, i) => (
                              <div key={i} className="text-[11px] text-[#D4D4D4]">
                                <span className="text-[#A3A3A3]">{h.changed_at ? new Date(h.changed_at).toLocaleString() : ''}</span>
                                {' · '}
                                <span className="text-white">{(h.changed_by_name || 'system')}</span>
                                {': '}
                                <span className="text-[#A3A3A3]">{(h.from || '').replace(/_/g, ' ')}</span>
                                {' → '}
                                <span className="text-[#D4AF37]">{(h.to || '').replace(/_/g, ' ')}</span>
                                {h.note ? <span className="text-[#A3A3A3]"> — {h.note}</span> : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* iter240 — Persistent Payment History (separate from QB workflow history). */}
                {isInvoice && (
                  <div data-testid="payment-history-section" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2">
                    <p className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1"><History className="w-3.5 h-3.5" /> Payment History</p>
                    {invoicePayments.length === 0 ? (
                      <p data-testid="payment-history-empty" className="text-[11px] text-[#525252]">
                        No detailed payment history was recorded in the Hub for this invoice. The existing paid fields and audit history still apply.
                      </p>
                    ) : (
                      <div className="space-y-2" data-testid="payment-history-list">
                        {invoicePayments.map((p) => (
                          <div key={p.id} data-testid={`payment-row-${p.id}`} className="border border-[#262626] rounded p-2 bg-[#141414]">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <span className="text-white text-sm font-bold">${Number(p.amount).toFixed(2)} — {p.method_label}</span>
                              <span className="text-[10px] text-[#525252]">{p.payment_date ? new Date(p.payment_date).toLocaleString() : ''}</span>
                            </div>
                            <p className="text-[11px] text-[#A3A3A3]">
                              Source: {p.source_label}
                              {p.processed_through === 'quickbooks' && p.source !== 'quickbooks' ? ' · Processed through: QuickBooks' : ''}
                              {p.reference ? ` · Ref: ${p.reference}` : ''}
                            </p>
                            <p className="text-[11px] text-[#A3A3A3]">Entered by {p.entered_by_name || '—'}{p.entered_at ? ` · ${new Date(p.entered_at).toLocaleString()}` : ''}</p>
                            <p className="text-[11px] text-[#737373]">Before ${Number(p.before_balance).toFixed(2)} → Total paid ${Number(p.resulting_total_paid).toFixed(2)} · Balance ${Number(p.resulting_balance).toFixed(2)}</p>
                            {p.notes ? <p className="text-[11px] text-[#A3A3A3] italic">“{p.notes}”</p> : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}


                {/* iter134 — Phase 2: Warranty/HSP claim packet + payment tracker. */}
                {isWarrantyInvoice && (
                  <div data-testid="hsp-panel" className="bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-md p-3 space-y-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Warranty Claim Packet</p>
                      <span
                        data-testid="hsp-status-badge"
                        className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${HSP_STATUS_COLOR[hspReviewStatus] || 'text-[#A3A3A3] bg-[#262626] border-[#262626]'}`}
                      >
                        {HSP_STATUS_LABEL[hspReviewStatus] || hspReviewStatus}
                      </span>
                    </div>

                    {/* Claim packet: residential homeowner + job info for office reference */}
                    <div data-testid="hsp-claim-packet" className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                      <div>
                        <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px]">Warranty Co. Billed</p>
                        <p className="text-white font-medium">{doc.customer_name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px]">HSP Work Order #</p>
                        <p className="text-white font-mono">{doc.hsp_work_order_number || doc.work_order_number || '—'}</p>
                      </div>
                      <div className="col-span-2 border-t border-[#262626] pt-2">
                        <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px]">Residential Homeowner</p>
                        <p className="text-white font-medium">{doc.homeowner_name || '—'}</p>
                        {doc.homeowner_address && <p className="text-[#A3A3A3] text-[11px]">{doc.homeowner_address}</p>}
                        <div className="flex gap-3 flex-wrap mt-1">
                          {doc.homeowner_phone && (
                            <a data-testid="hsp-homeowner-phone" href={`tel:${doc.homeowner_phone}`} className="text-[#D4AF37] hover:underline text-[11px]">
                              📞 {doc.homeowner_phone}
                            </a>
                          )}
                          {doc.homeowner_email && (
                            <a data-testid="hsp-homeowner-email" href={`mailto:${doc.homeowner_email}`} className="text-[#D4AF37] hover:underline text-[11px]">
                              ✉ {doc.homeowner_email}
                            </a>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px]">Job Date</p>
                        <p className="text-white">{fmtLocalDate(doc.service_date || doc.date) || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px]">Technician</p>
                        <p className="text-white">{doc.technician_name || '—'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px]">Work Performed</p>
                        <p className="text-white whitespace-pre-wrap text-[11px]">{doc.description || '—'}</p>
                      </div>
                      <div className="col-span-2 border-t border-[#262626] pt-2 flex items-center justify-between">
                        <span className="text-[#A3A3A3]">HSP Amount Owed</span>
                        <span className="text-[#D4AF37] font-bold">${Number(doc.amount || 0).toFixed(2)}</span>
                      </div>
                      {amountPaid > 0 && (
                        <div className="col-span-2 flex items-center justify-between">
                          <span className="text-green-400">Amount Paid (HSP)</span>
                          <span className="text-green-400 font-bold">${amountPaid.toFixed(2)}</span>
                        </div>
                      )}
                      {remainingBalance > 0 && amountPaid > 0 && (
                        <div className="col-span-2 flex items-center justify-between">
                          <span className="text-orange-300">Remaining</span>
                          <span className="text-orange-300 font-bold">${remainingBalance.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {/* HSP status actions */}
                    {hasPermission('edit_invoices') && hspReviewStatus === 'pending_office_review' && (
                      <Button
                        data-testid="hsp-mark-submitted"
                        onClick={() => handleHspStatusChange('submitted_to_hsp')}
                        disabled={hspSavingStatus}
                        variant="outline"
                        className="w-full h-10 border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20"
                      >
                        {hspSavingStatus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Mark Submitted to HSP
                      </Button>
                    )}

                    {hasPermission('mark_invoice_paid') && hspReviewStatus !== 'hsp_paid' && status !== 'refunded' && status !== 'void' && (
                      <Button
                        data-testid="hsp-mark-paid-open"
                        onClick={() => setShowHspPaid(true)}
                        className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-bold"
                      >
                        <DollarSign className="w-4 h-4 mr-2" /> Mark HSP Paid
                      </Button>
                    )}

                    {/* Recorded HSP payments history */}
                    {hspPaymentsList.length > 0 && (
                      <div data-testid="hsp-payments-history" className="border-t border-[#262626] pt-2 space-y-1">
                        <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px]">HSP Payments Recorded</p>
                        {hspPaymentsList.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-[11px] text-[#D4D4D4]">
                            <span>
                              {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : ''} · {(p.method || '').toUpperCase()}
                              {p.reference ? ` #${p.reference}` : ''}
                            </span>
                            <span className="text-green-400 font-bold">${Number(p.amount || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* iter91 — Send by Email (prominent at the top of Staff Actions). Works for both
                    invoices and estimates. Same single record / payment link is referenced.
                    iter133 — Phase 1: hidden while we defer customer-facing billing to QuickBooks. */}
                {!HIDE_NEXFLOW_BILLING_ACTIONS && hasPermission('generate_payment_links') && (
                  <Button
                    data-testid="staff-send-email"
                    onClick={openSendModal}
                    className="w-full h-11 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
                  >
                    <Send className="w-4 h-4 mr-2" /> {isInvoice ? 'Send Invoice by Email' : 'Send Estimate by Email'}
                  </Button>
                )}

                {/* Apply promo / referral code (staff-side, before payment) */}
                {hasPermission('apply_promo_codes') && status !== 'paid' && status !== 'refunded' && status !== 'void' && (
                  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
                    <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">Apply Promo / Referral Code</p>
                    {doc.promo_code ? (
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <span className="text-[#D4AF37] font-mono font-bold">{doc.promo_code}</span>
                          <span className="text-green-400 text-xs ml-2">— {doc.discount_percent || 10}% applied</span>
                        </div>
                        {isInvoice && (
                          <Button data-testid="staff-remove-code" onClick={handleStaffRemoveCode} variant="outline" size="sm" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white">Remove</Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input data-testid="staff-promo-input" value={staffPromoInput} onChange={(e) => setStaffPromoInput(e.target.value.toUpperCase())} placeholder="NEXF-CODY1" className="bg-[#141414] border-[#262626] text-white uppercase font-mono" />
                        <Button data-testid="staff-apply-code" onClick={handleStaffApplyCode} disabled={staffApplyingCode || !staffPromoInput.trim()} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                          {staffApplyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    )}
                    <p className="text-[10px] text-[#525252] mt-1">Codes cannot stack with member discount.</p>
                  </div>
                )}

                {/* Invoice-only payment workflow tools */}
                {isInvoice && (
                  <>
                    {/* iter133 — Phase 1: hide Stripe-driven staff payment actions; QuickBooks owns billing. */}
                    {!HIDE_NEXFLOW_BILLING_ACTIONS && hasPermission('collect_payments') && status !== 'paid' && status !== 'refunded' && status !== 'void' && (
                      <Button
                        data-testid="staff-charge-card-now"
                        onClick={handleChargeCardNow}
                        className="bg-green-600 hover:bg-green-700 text-white w-full h-12 text-base font-bold"
                      >
                        <CreditCard className="w-5 h-5 mr-2" /> Charge Card on This Device
                      </Button>
                    )}
                    {!HIDE_NEXFLOW_BILLING_ACTIONS && hasPermission('generate_payment_links') && status !== 'paid' && status !== 'refunded' && status !== 'void' && (
                      <div className="flex flex-wrap gap-2">
                        <Button data-testid="staff-payment-link" onClick={handleGetPaymentLink} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]">
                          <Link2 className="w-3.5 h-3.5 mr-1" /> Copy Payment Link
                        </Button>
                        <Button data-testid="staff-qr-toggle" onClick={() => setShowQr(!showQr)} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]">
                          <QrCode className="w-3.5 h-3.5 mr-1" /> {showQr ? 'Hide QR' : 'Show QR'}
                        </Button>
                      </div>
                    )}
                    {!HIDE_NEXFLOW_BILLING_ACTIONS && paymentLink && (
                      <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2 text-[11px] text-[#A3A3A3] break-all">{paymentLink}</div>
                    )}
                    {!HIDE_NEXFLOW_BILLING_ACTIONS && showQr && (
                      <div data-testid="staff-qr-image" className="bg-white inline-block p-2 rounded">
                        <img alt="Payment QR" width="180" height="180" src={`${API}/invoices/${doc.id}/qr-code?ts=${Date.now()}`} />
                      </div>
                    )}

                    {hasPermission('mark_invoice_paid') && !isWarrantyInvoice && status !== 'paid' && status !== 'refunded' && status !== 'void' && (
                      <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2">
                        <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Record Payment</p>
                        {amountPaid > 0.01 && (
                          <div data-testid="record-payment-existing-balance" className="text-[11px] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded px-2 py-1">
                            Already recorded: ${amountPaid.toFixed(2)} · Balance due ${remainingBalance.toFixed(2)}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-[10px] uppercase text-[#525252] mb-1">Method</p>
                            <Select value={markPaidMethod} onValueChange={setMarkPaidMethod}>
                              <SelectTrigger data-testid="mark-paid-method" className="bg-[#141414] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#141414] border-[#262626]">
                                <SelectItem value="cash" className="text-white">Cash</SelectItem>
                                <SelectItem value="check" className="text-white">Check</SelectItem>
                                <SelectItem value="card" className="text-white">Card</SelectItem>
                                <SelectItem value="ach" className="text-white">ACH / Bank</SelectItem>
                                <SelectItem value="quickbooks" className="text-white">QuickBooks Payment</SelectItem>
                                <SelectItem value="financing" className="text-white">Financing</SelectItem>
                                <SelectItem value="other" className="text-white">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase text-[#525252] mb-1">Source</p>
                            <Select value={markPaidSource} onValueChange={setMarkPaidSource}>
                              <SelectTrigger data-testid="mark-paid-source" className="bg-[#141414] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#141414] border-[#262626]">
                                <SelectItem value="field" className="text-white">Field / Technician</SelectItem>
                                <SelectItem value="office" className="text-white">Office</SelectItem>
                                <SelectItem value="quickbooks" className="text-white">QuickBooks</SelectItem>
                                <SelectItem value="financing" className="text-white">Financing</SelectItem>
                                <SelectItem value="other" className="text-white">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            data-testid="mark-paid-ref"
                            value={markPaidRef}
                            onChange={(e) => setMarkPaidRef(e.target.value)}
                            placeholder={markPaidMethod === 'check' ? 'Check # *' : 'Reference (optional)'}
                            className="bg-[#141414] border-[#262626] text-white"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            data-testid="mark-paid-amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={markPaidAmount}
                            onChange={(e) => setMarkPaidAmount(e.target.value)}
                            placeholder={`Amount (default $${remainingBalance.toFixed(2)})`}
                            className="bg-[#141414] border-[#262626] text-white"
                          />
                          <Input
                            data-testid="mark-paid-date"
                            type="date"
                            value={markPaidDate}
                            onChange={(e) => setMarkPaidDate(e.target.value)}
                            className="bg-[#141414] border-[#262626] text-white"
                          />
                        </div>
                        <Textarea
                          data-testid="mark-paid-notes"
                          value={markPaidNotes}
                          onChange={(e) => setMarkPaidNotes(e.target.value)}
                          placeholder="Notes (optional)"
                          rows={2}
                          className="bg-[#141414] border-[#262626] text-white"
                        />
                        {/* iter140 — Phase 5: residential receipt opt-out (defaults set per payor_kind in the doc-load useEffect). */}
                        {(() => {
                          const _pk = (doc?.payor_kind || '').toLowerCase();
                          const _pt = (doc?.payor_type || '').toLowerCase();
                          const _nonResidential = ['warranty', 'commercial', 'construction', 'internal'].includes(_pk) ||
                                                  ['warranty', 'commercial', 'construction', 'internal'].includes(_pt) ||
                                                  (doc?.invoice_status || '').startsWith('hsp');
                          const hasEmail = !!(doc?.customer_email || '').trim();
                          if (_nonResidential) return null;   // hidden for warranty/commercial/construction
                          return (
                            <label className="flex items-center gap-2 cursor-pointer text-xs">
                              <input
                                data-testid="mark-paid-send-receipt"
                                type="checkbox"
                                checked={markPaidSendReceipt && hasEmail}
                                disabled={!hasEmail}
                                onChange={(e) => setMarkPaidSendReceipt(e.target.checked)}
                                className="accent-[#D4AF37]"
                              />
                              <span className={hasEmail ? 'text-white' : 'text-[#525252]'}>
                                Send receipt to customer{hasEmail ? '' : ' (no email on file)'}
                              </span>
                            </label>
                          );
                        })()}
                        <Button data-testid="staff-mark-paid" onClick={handleStaffMarkPaid} className="bg-green-600 hover:bg-green-700 text-white w-full">
                          <DollarSign className="w-4 h-4 mr-1" /> Record Payment {markPaidAmount ? `$${Number(markPaidAmount).toFixed(2)}` : `$${remainingBalance.toFixed(2)}`}
                        </Button>
                      </div>
                    )}

                    {hasPermission('issue_refunds') && (status === 'paid') && (
                      <div>
                        {!showRefund ? (
                          <Button data-testid="staff-refund-toggle" onClick={() => setShowRefund(true)} variant="outline" size="sm" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white">
                            <Undo2 className="w-3.5 h-3.5 mr-1" /> Issue Refund
                          </Button>
                        ) : (
                          <div className="bg-[#0A0A0A] border border-red-700/40 rounded-md p-3 space-y-2">
                            <p className="text-red-400 text-xs uppercase tracking-wider">Issue Refund</p>
                            <Input data-testid="refund-amount" type="number" step="0.01" min="0" max={doc.amount} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder={`Amount (default full: $${(doc.amount || 0).toFixed(2)})`} className="bg-[#141414] border-[#262626] text-white" />
                            <Textarea data-testid="refund-reason" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Reason (required)" rows={2} className="bg-[#141414] border-[#262626] text-white" />
                            <div className="flex gap-2">
                              <Button data-testid="staff-refund-confirm" onClick={handleStaffRefund} className="bg-red-600 hover:bg-red-700 text-white flex-1">Confirm Refund</Button>
                              <Button onClick={() => setShowRefund(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-[#262626] flex items-center justify-between gap-2 sticky bottom-0 bg-[#141414] pb-2">
            {canEdit ? (
              editing ? (
                <>
                  <Button data-testid="detail-save" onClick={handleSave} disabled={saving} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1"><Save className="w-4 h-4 mr-2" /> Save</Button>
                  <Button data-testid="detail-edit-cancel" onClick={() => {
                    editDraft.clear(); setEditing(false);
                    const b = savedSnapRef.current;   // last successful save beats a stale `doc` prop
                    if (b) { setLineItems(b.lineItems); setNotes(b.notes); setStatus(b.status); setDepositAmount(b.depositAmount); return; }
                    setLineItems(normalizeLineItems(doc)); setNotes(doc.notes || doc.description || ''); setStatus(doc.status); setDepositAmount(doc.deposit_amount || 0);
                  }} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
                </>
              ) : (
                <>
                  <Button data-testid="detail-edit" onClick={() => setEditing(true)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1"><Edit2 className="w-4 h-4 mr-2" /> Edit</Button>
                  <Button data-testid="detail-delete" onClick={handleDelete} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white"><Trash2 className="w-4 h-4" /></Button>
                </>
              )
            ) : (
              <Button onClick={() => onOpenChange(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626] flex-1"><X className="w-4 h-4 mr-2" /> Close</Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* iter134 — Phase 2: Mark HSP Paid modal. */}
      <Dialog open={showHspPaid} onOpenChange={setShowHspPaid}>
        <DialogContent data-testid="hsp-mark-paid-modal" className="bg-[#141414] border-[#262626] text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#D4AF37]" />
              Mark HSP Paid
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-xs space-y-1">
              <div><span className="text-[#525252]">Warranty Co.:</span> <span className="text-white">{doc.customer_name || '—'}</span></div>
              <div><span className="text-[#525252]">Homeowner:</span> <span className="text-white">{doc.homeowner_name || '—'}</span></div>
              <div><span className="text-[#525252]">HSP WO#:</span> <span className="text-white font-mono">{doc.hsp_work_order_number || '—'}</span></div>
              <div><span className="text-[#525252]">Amount Owed:</span> <span className="text-[#D4AF37] font-bold">${Number(doc.amount || 0).toFixed(2)}</span></div>
              {amountPaid > 0 && <div><span className="text-[#525252]">Already Paid:</span> <span className="text-green-400 font-bold">${amountPaid.toFixed(2)}</span></div>}
              {remainingBalance > 0 && amountPaid > 0 && <div><span className="text-[#525252]">Remaining:</span> <span className="text-orange-300 font-bold">${remainingBalance.toFixed(2)}</span></div>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-white text-xs">Amount Received *</Label>
                <Input
                  data-testid="hsp-amount-input"
                  type="number" step="0.01" min="0"
                  value={hspAmount}
                  onChange={(e) => setHspAmount(e.target.value)}
                  placeholder={remainingBalance > 0 ? remainingBalance.toFixed(2) : Number(doc.amount || 0).toFixed(2)}
                  className="bg-[#0A0A0A] border-[#262626] text-white h-10"
                />
              </div>
              <div>
                <Label className="text-white text-xs">Payment Date</Label>
                <Input
                  data-testid="hsp-date-input"
                  type="date"
                  value={hspDate}
                  onChange={(e) => setHspDate(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white h-10"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-white text-xs">Method *</Label>
                <Select value={hspMethod} onValueChange={setHspMethod}>
                  <SelectTrigger data-testid="hsp-method-select" className="bg-[#0A0A0A] border-[#262626] text-white h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="check" className="text-white">Check</SelectItem>
                    <SelectItem value="ach" className="text-white">ACH / Direct Deposit</SelectItem>
                    <SelectItem value="wire" className="text-white">Wire</SelectItem>
                    <SelectItem value="other" className="text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white text-xs">
                  {hspMethod === 'check' ? 'Check # *' : hspMethod === 'other' ? 'Reference (optional)' : 'Reference / Trans # *'}
                </Label>
                <Input
                  data-testid="hsp-reference-input"
                  value={hspReference}
                  onChange={(e) => setHspReference(e.target.value)}
                  placeholder={hspMethod === 'check' ? '12345' : hspMethod === 'other' ? 'optional' : 'TRX-…'}
                  className="bg-[#0A0A0A] border-[#262626] text-white h-10"
                />
              </div>
            </div>
            <div>
              <Label className="text-white text-xs">Notes</Label>
              <Textarea
                data-testid="hsp-notes-input"
                value={hspNotes}
                onChange={(e) => setHspNotes(e.target.value)}
                rows={2}
                placeholder="Optional internal note (e.g. short-pay reason)"
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="hsp-mark-paid-cancel" variant="outline" onClick={() => setShowHspPaid(false)} className="flex-1 border-[#262626] text-white hover:bg-[#262626]">
                Cancel
              </Button>
              <Button data-testid="hsp-mark-paid-confirm" onClick={handleHspMarkPaid} disabled={hspSavingPaid} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold">
                {hspSavingPaid ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                Record HSP Payment
              </Button>
            </div>
            <p className="text-[10px] text-[#525252]">
              Recording the full amount marks the warranty invoice as paid. Partial payments leave the
              status as &quot;Partially Paid&quot; until the remaining balance is received. QuickBooks remains
              the official accounting source — this only tracks operational HSP payment status.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter91 — Send by Email modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent data-testid="send-email-modal" className="bg-[#141414] border-[#262626] text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
              {isInvoice ? 'Send Invoice by Email' : 'Send Estimate by Email'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-xs space-y-1">
              <div><span className="text-[#525252]">Customer:</span> <span className="text-white">{doc.customer_name || '—'}</span></div>
              <div><span className="text-[#525252]">Email on file:</span> <span className="text-white">{doc.customer_email || <span className="text-red-400">—</span>}</span></div>
              <div><span className="text-[#525252]">Phone:</span> <span className="text-white">{doc.customer_phone || <span className="text-[#A3A3A3]">—</span>}</span></div>
              <div><span className="text-[#525252]">{isInvoice ? 'Invoice' : 'Estimate'} #:</span> <span className="text-white font-mono">{number || '—'}</span></div>
              <div><span className="text-[#525252]">Total:</span> <span className="text-[#D4AF37] font-bold">${(doc.amount || 0).toFixed(2)}</span></div>
            </div>
            <div>
              <Label className="text-white text-xs">Email recipient</Label>
              <Input
                data-testid="send-email-to"
                type="email"
                value={sendToEmail}
                onChange={(e) => setSendToEmail(e.target.value)}
                placeholder="customer@example.com"
                className="bg-[#0A0A0A] border-[#262626] text-white h-11"
              />
            </div>
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-xs text-[#A3A3A3] space-y-1">
              <p className="text-[#D4AF37] font-bold text-[10px] uppercase tracking-wider mb-1">Email preview</p>
              <p>Subject: <span className="text-white">NexFlow Plumbing — {isInvoice ? 'Invoice' : 'Estimate'} {number} (${(doc.amount || 0).toFixed(2)})</span></p>
              <p>Hi {(doc.customer_name || 'Customer').split(' ')[0]}, your {isInvoice ? 'invoice' : 'estimate'} from NexFlow Plumbing is ready.</p>
              {isInvoice && <p className="text-[#525252] italic">A &quot;View &amp; Pay Invoice&quot; button (linked to the same invoice record) will be included.</p>}
            </div>
            <div className="flex items-start gap-2 bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
              <input
                id="send-include-sms"
                data-testid="send-include-sms"
                type="checkbox"
                checked={sendIncludeSms}
                onChange={(e) => setSendIncludeSms(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#D4AF37]"
              />
              <label htmlFor="send-include-sms" className="text-xs text-white cursor-pointer flex-1">
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-[#D4AF37]" /> Text customer that {isInvoice ? 'invoice' : 'estimate'} was emailed
                </span>
                <span className="block text-[10px] text-[#525252] mt-0.5">
                  Sent only if email succeeds, customer has a phone number, and they haven&apos;t opted out of SMS.
                </span>
              </label>
            </div>
            {sendIncludeSms && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#525252]">SMS message (optional override)</Label>
                <Textarea
                  data-testid="send-sms-message"
                  value={sendSmsMessage}
                  onChange={(e) => setSendSmsMessage(e.target.value)}
                  rows={2}
                  placeholder={`Hi ${(doc.customer_name || 'there').split(' ')[0]}, NexFlow just emailed your ${isInvoice ? 'invoice' : 'estimate'} to ${sendToEmail || 'your email'}. Please check your inbox. If you have any questions, call us at 614-285-4502.`}
                  className="bg-[#0A0A0A] border-[#262626] text-white text-xs"
                />
              </div>
            )}
            {typoSuggestion && (
              <div data-testid="send-email-typo-warning"
                   className="rounded-md border border-[#D4AF37] bg-[#D4AF37]/10 p-3 text-xs space-y-2">
                <p className="font-bold text-[#D4AF37] flex items-center gap-1.5">
                  <Mail className="w-4 h-4" /> Did you mean <span className="font-mono">{typoSuggestion.suggestion}</span>?
                </p>
                <p className="text-[#A3A3A3]">{typoSuggestion.reason}</p>
                <p className="text-[#A3A3A3]">SendGrid would accept <span className="font-mono text-white">{typoSuggestion.to}</span> at the API level then silently bounce on delivery. Pick one:</p>
                <div className="flex gap-2 flex-wrap pt-1">
                  <Button
                    data-testid="send-email-use-suggestion"
                    onClick={() => {
                      const corrected = typoSuggestion.suggestion;
                      setSendToEmail(corrected);
                      setTypoSuggestion(null);
                      handleSendEmail({ forceSend: true, updateCustomer: true, overrideTo: corrected });
                    }}
                    className="h-9 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-xs font-bold"
                  >
                    Use Suggestion &amp; Fix Customer
                  </Button>
                  <Button
                    data-testid="send-email-send-anyway"
                    onClick={() => handleSendEmail({ forceSend: true, updateCustomer: false })}
                    variant="outline"
                    className="h-9 border-[#7F1D1D] text-red-300 hover:bg-[#7F1D1D]/20 text-xs"
                  >
                    Send Anyway
                  </Button>
                  <Button
                    onClick={() => setTypoSuggestion(null)}
                    variant="outline"
                    className="h-9 border-[#262626] text-white hover:bg-[#262626] text-xs"
                  >
                    Edit Address
                  </Button>
                </div>
              </div>
            )}
            {sendResult && (
              <div data-testid="send-result"
                   className={`rounded-md border p-3 text-xs space-y-1 ${sendResult?.email?.sent ? 'bg-green-500/10 border-green-700 text-green-300' : 'bg-[#7F1D1D]/15 border-[#7F1D1D] text-red-300'}`}>
                <p className="font-bold">{sendResult?.email?.sent ? '✓ Email sent' : '✗ Email failed'}</p>
                {sendResult?.email?.error && <p className="break-words whitespace-pre-wrap">{sendResult.email.error}</p>}
                {sendResult?.email?.from_email && <p className="text-[#A3A3A3]">From: {sendResult.email.from_email}</p>}
                {sendResult?.sms && (
                  <p className="mt-1 pt-1 border-t border-current/30">
                    SMS: {sendResult.sms.sent ? '✓ sent' : `✗ ${sendResult.sms.error || sendResult.sms.reason || 'skipped'}`}
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="send-email-submit"
                onClick={() => handleSendEmail()}
                disabled={sending}
                className="flex-1 h-11 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send</>}
              </Button>
              <Button
                onClick={() => setShowSendModal(false)}
                variant="outline"
                className="border-[#262626] text-white hover:bg-[#262626] h-11"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter116 — Admin Completion Correction Sheet. Opens from the Correct
          Completion button in Staff Actions above. */}
      <AdminCompletionEditSheet
        open={!!editingCompletionId}
        onOpenChange={(o) => { if (!o) setEditingCompletionId(null); }}
        completionId={editingCompletionId}
        onSaved={() => { onChange?.(); }}
      />

      {/* iter197 — Correct Payment modal. */}
      <Dialog open={showCorrectPayment} onOpenChange={setShowCorrectPayment}>
        <DialogContent data-testid="correct-payment-modal" className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Wallet className="w-5 h-5 text-blue-300" /> Correct Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Current snapshot */}
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 grid grid-cols-2 gap-2 text-sm">
              <div><p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Invoice Total</p><p className="text-white font-bold">${Number(doc.amount || 0).toFixed(2)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Current Amount Paid</p><p className="text-white font-bold">${Number(doc.amount_paid || 0).toFixed(2)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Current Balance</p><p className="text-white font-bold">${Math.max(0, Number(doc.amount || 0) - Number(doc.amount_paid || 0)).toFixed(2)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Current Method</p><p className="text-white">{formatPaymentMethod(doc.payment_method, doc.payment_method_detail)}</p></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Corrected Total Paid to Date *</Label>
                <Input data-testid="correct-amount" type="number" step="0.01" min="0" value={corrAmount} onChange={(e) => setCorrAmount(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Collected On Site</Label>
                <Input data-testid="correct-onsite" type="number" step="0.01" min="0" value={corrOnSite} onChange={(e) => setCorrOnSite(e.target.value)} placeholder="Money the tech collected on the visit" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Payment Disposition</Label>
                <Select value={corrDisposition || 'unset'} onValueChange={(v) => setCorrDisposition(v === 'unset' ? '' : v)}>
                  <SelectTrigger data-testid="correct-disposition" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="unset" className="text-white">— Unchanged —</SelectItem>
                    <SelectItem value="paid_cash" className="text-white">Customer Paid — Cash</SelectItem>
                    <SelectItem value="paid_check" className="text-white">Customer Paid — Check</SelectItem>
                    <SelectItem value="paid_other" className="text-white">Customer Paid — Other</SelectItem>
                    <SelectItem value="deferred_unable_collect" className="text-white">No Payment Collected — Office to Bill</SelectItem>
                    <SelectItem value="no_balance_due" className="text-white">No Customer Balance Due</SelectItem>
                    <SelectItem value="financing" className="text-white">Financing Used</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Payment Method</Label>
                <Select value={corrMethod || 'unset'} onValueChange={(v) => setCorrMethod(v === 'unset' ? '' : v)}>
                  <SelectTrigger data-testid="correct-method" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="unset" className="text-white">— Unchanged —</SelectItem>
                    <SelectItem value="cash" className="text-white">Cash</SelectItem>
                    <SelectItem value="check" className="text-white">Check</SelectItem>
                    <SelectItem value="card" className="text-white">Card</SelectItem>
                    <SelectItem value="card_in_person" className="text-white">Card (in person)</SelectItem>
                    <SelectItem value="other" className="text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Method Detail</Label>
                <Input data-testid="correct-method-detail" value={corrMethodDetail} onChange={(e) => setCorrMethodDetail(e.target.value)} placeholder="e.g. quickbooks_pay, zelle" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reference #</Label>
                <Input data-testid="correct-reference" value={corrReference} onChange={(e) => setCorrReference(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Paid Date</Label>
                <Input data-testid="correct-date" type="date" value={corrDate} onChange={(e) => setCorrDate(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Status Override</Label>
                <Select value={corrStatus || 'auto'} onValueChange={(v) => setCorrStatus(v === 'auto' ? '' : v)}>
                  <SelectTrigger data-testid="correct-status" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="auto" className="text-white">Auto (from amount)</SelectItem>
                    <SelectItem value="paid" className="text-white">Paid</SelectItem>
                    <SelectItem value="deposit_paid" className="text-white">Partial / Balance Due</SelectItem>
                    <SelectItem value="pending" className="text-white">Pending / Unpaid</SelectItem>
                    <SelectItem value="overdue" className="text-white">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Payment Notes</Label>
              <Textarea data-testid="correct-notes" value={corrNotes} onChange={(e) => setCorrNotes(e.target.value)} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>

            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Correction Reason *</Label>
              <Select value={corrReason} onValueChange={setCorrReason}>
                <SelectTrigger data-testid="correct-reason" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {CORRECTION_REASONS.map((r) => <SelectItem key={r.v} value={r.v} className="text-white">{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {corrReason === 'other' && (
              <Input data-testid="correct-reason-note" value={corrReasonNote} onChange={(e) => setCorrReasonNote(e.target.value)} placeholder="Describe the correction" className="bg-[#0A0A0A] border-[#262626] text-white" />
            )}

            {/* Live preview */}
            <div className="text-xs text-[#A3A3A3] bg-[#0A0A0A] border border-[#262626] rounded p-2">
              New balance: <span className="text-white font-bold">${Math.max(0, Number(doc.amount || 0) - Number(corrAmount || 0)).toFixed(2)}</span>
              {' · '}Status: <span className="text-white font-bold">{corrStatus ? corrStatus.replace('_', ' ') : (Number(corrAmount || 0) >= Number(doc.amount || 0) && Number(doc.amount || 0) > 0 ? 'paid' : (Number(corrAmount || 0) <= 0 ? 'pending' : 'partial'))}</span>
            </div>

            <div className="flex gap-2 pt-1">
              <Button data-testid="correct-payment-confirm" onClick={handleCorrectPayment} disabled={corrSaving} className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold">
                {corrSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Correction</>}
              </Button>
              <Button data-testid="correct-payment-cancel" onClick={() => setShowCorrectPayment(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-11">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BillingDetailSheet;
